// bootResolve — boot step 2 of 3, ASYNC, mounts nothing.
//
// See bootPrepare.js for the one-boot design. This step waits for the SDK
// init, decides the theme mode (stored → user record → tenant default), runs
// the AUTH GATE (composite /core/boot or getMe; a rejected token logs out and
// bounces to /login), builds the auth snapshot (context.preResolvedAuth),
// reads the active org + workspace, and resolves + loads the interface
// language (step 7b).
//
// `state` is the object the boot writes its seed into: the parcel boot hands
// `context.state` (create() turns it into the root state); the published boot
// hands a fresh seed — the root state already exists there — and bootApply
// writes it onto the live root.
//
// Inputs: the context, bootPrepare's result, { state, publicSurface,
//   warmSnapshot, getWorkspaceCached, primeWorkspaceCache, apps, defaultApp,
//   resolvedManifest, fallbackRoute, mountEarly } — `mountEarly(themeMode)`
//   is the parcel composer's warm-start mount (null on the published shell).
// Returns { aborted, themeMode, lang, earlyShell, state, storage, completeWorkspaceAccess }:
//   aborted = the boot reached no session (a signed-out visitor, or a rejected one);
//   navigated = it also left the page (logout + /login, or the fresh-token reload);
//   completeWorkspaceAccess = null, or `(liveRoot) => Promise<boolean>` while the
//     boot's workspace record still owes the caller's `access` (step 7): hand it
//     the mounted root and the background read's answer reaches that root's
//     record through the record owner (true = merged).
import { awaitSDKInit } from './sdk.js'
import { realtimeProvider } from './sdkRealtimeProvider.js'
import { diagnoseAuth, runAuthDiagnostic } from './diagnoseAuth.js'
import { purgeAuthStorage } from './clearAuthStorage.js'
import { reacquireBridgeSession } from './sessionBridgeReacquire.js'
import { isSessionBridgeBorrower } from './sessionBridgePeerOrigin.js'
import { resolveAuthNext } from './resolveAuthNext.js'
import { currentChannelFromHost } from './canvasUrl.js'
import {
  hasNoStoredThemeMode,
  resolveBootThemeMode,
  writeStoredThemeMode,
  userHasOwnThemeChoice,
  readTenantBootThemeMode,
  workspaceThemeModeOf,
  bootTenantThemeMode
} from './themeMode.js'
import { bootTrail } from './bootTrail.js'
import { resolveThemeResolvedNow } from './themeResolved.js'
import { GUEST_ROUTE_RE } from './guestRoutes.js'
import { DEFAULT_LANG, enforcedWorkspaceLang, langDir, resolveLang } from './languages.js'
import {
  loadI18nNamespaces,
  hasI18nBundle,
  mergeAllNamespaces
} from './i18nLoader.js'
import { applyPublicBootLang, settleWhitelabelBrand } from './publicBootLang.js'
import {
  BOOT_CALL_DEADLINE_MS,
  withBootDeadline,
  isBootDeadline,
  publishBootFailure
} from './bootDeadline.js'
import { isMwtEnabled } from './mwtFlag.js'
import { visibleOrgs } from './visibleOrgs.js'
import {
  resolveActiveWorkspace,
  getActiveWorkspacePointer,
  setActiveWorkspacePointer
} from './activeWorkspacePointer.js'
import {
  scopeSnapshotToWhitelabelHost,
  scopeWorkspaceListToWhitelabelHost
} from './whitelabelScope.js'
import {
  getWorkspaceRawSegments,
  getWorkspaceQueryParam,
  routePathCandidates
} from './workspacePrefix.js'
import { syncWorkspaceUrl } from './workspaceUrlSync.js'
import {
  normalizeWorkspaceRecord,
  setActiveWorkspaceRecord,
  stampWorkspaceShape,
  stampWorkspaceListShape,
  workspaceDisplayName
} from './activeWorkspaceRecord.js'

export async function bootResolve(context, prep, opts = {}) {
  const {
    publicSurface = false,
    warmSnapshot = null,
    getWorkspaceCached = null,
    primeWorkspaceCache = null,
    apps = null,
    defaultApp = null,
    resolvedManifest = null,
    fallbackRoute = '/',
    mountEarly = null,
    // every network await of the gate is bounded (bootDeadline.js); a
    // caller (a test) may shorten it through `context.bootDeadlineMs`
    callDeadlineMs = (context && context.bootDeadlineMs) || BOOT_CALL_DEADLINE_MS
  } = opts
  const state = opts.state || context.state
  // WHITELABEL-STRICT-TENANT-SCOPE-1 — the TENANT a white-label host binds
  // this boot to, independent of its branding: `state.whitelabelHostScope`
  // (the scope object, or `false` = a customer host whose tenant could not be
  // established → no tenant renders), seeded by the workspace entry before
  // bootShell runs. The brand's ids stay the fallback for an entry that
  // predates that seed. `null` on platform hosts — every caller below is then
  // byte-identical to before. Inline (not module scope) for the same frank
  // reason as isTokenRejection below.
  const whitelabelHostScopeOf = () => {
    if (!state) return null
    const scope = state.whitelabelHostScope
    if (scope === false) return false
    if (scope && scope.organizationId) return scope
    const brand = state.whitelabelBrand
    return brand && brand.organizationId ? brand : null
  }
  const sdk = prep && prep.sdk
  const lang = prep && prep.lang
  const storage = prep && prep.storage
  const _cachedWsLanguage = prep && prep.cachedWsLanguage
  const _poly = (prep && prep.poly) || context.polyglot
  let themeMode = prep && prep.themeMode

  // THE SESSION'S TEXT SETS — the i18n namespaces a boot declares into
  // `_poly.namespaces`, the set `setLang` loads on every later language
  // switch. ONE computation for the TWO places that declare it:
  //   • step 7b, on every boot that reaches a session or a public surface;
  //   • the soft /login bounce of the auth gate, for a signed-out visitor.
  // That bounce returns before 7b. It used to leave bootPrepare's baseline
  // declared, so a sign-in on the login form (no reload) into a Georgian
  // workspace switched to ka and loaded the baseline only: twelve of the
  // shell's sets (canvas, chat, mail, meet, …) stayed English until a reload
  // (AI overhaul, sidebar finding 3 — Nika 2026-10-28, "fix in shared").
  //
  // It reads the CURRENT `window.location.pathname`, as 7b always did. The
  // bounce calls it BEFORE its replaceState, so it reads the page the visitor
  // asked for — the page `?next=` returns to after the sign-in.
  //
  // An inline const arrow, not a module-scope helper: the same frank reason
  // (FA204) as whitelabelHostScopeOf above.
  const sessionI18nNamespaces = () => {
    // Which namespaces this session needs. `common` always; then the running
    // app's own, from its manifest's `i18nNamespaces`; then `auth` on the
    // logged-out/login surfaces and `account` + `settings` where the account
    // pages boot.
    //
    // THIS IS THE MECHANISM, not a stub: the set computed here is what boot
    // loads and what a later `setLang` re-loads. Manifest namespaces are
    // INTERSECTED with the loader's registry (hasI18nBundle on the English base, derived by
    // `scripts/gen-i18n-registry.mjs` from what is on disk) so a manifest can
    // never ask for a bundle nobody has written. Adding `i18n/en/<ns>.json` and
    // running the generator is the whole of what makes a manifest's declaration
    // go live — there is no second list to update.
    //
    // ⚠️ THE INTERSECTION MUST BE LOUD. It used to drop an unknown namespace
    // in silence, which is exactly what hid the workspace shell declaring
    // `i18nNamespaces: ['workspace-project']` — a bundle nobody has written.
    // The shell therefore loaded `common` alone in every non-English language,
    // and a correctly-resolved Georgian boot still rendered an English page
    // because almost nothing on it was in `common`. A namespace a surface
    // declares must either LOAD or SAY SO. Warned once per (manifest,
    // namespace) per session, not per boot step, so it reads as one actionable
    // line rather than noise.
    //
    // Seeded from whatever the context already carries — the shared library
    // declares the shipped set, so a context bootShell never ran for still has
    // one. Route matching then NARROWS it, inside its own guard: it reads
    // manifests supplied by the caller, so a malformed `routes` entry is the
    // one thing here that could throw on input this function does not control,
    // and losing all of 7b to it would take the language config down with it.
    const _namespaces = new Set(['common', ...(_poly.namespaces || [])])
    try {
      const _path = typeof window !== 'undefined' ? window.location.pathname : fallbackRoute || '/'
      // ⚠️ MATCH THE APP PATH, NOT THE BROWSER PATH.
      //
      // A manifest's `routes` are written against the APP path
      // (`/^\/(tickets|chat|…)/`), while the browser URL carries the
      // `/w/<org>[/<ws-handle>]` tenancy prefix. Matched raw, NO manifest route
      // ever matched under `/w/…` — in silence — so the namespace set stayed at
      // its seed and a Georgian tickets board rendered its own strings in
      // English inside a translated shell. `routePathCandidates` offers the
      // readings, most specific first; the FIRST that hits any manifest wins,
      // so a stripped candidate can never eat a real first segment.
      const _candidates = routePathCandidates(_path)
      const _all = resolvedManifest ? [resolvedManifest] : Object.values(apps || {})
      const _hits = (p) =>
        _all.filter((m) =>
          ((m && m.routes) || []).some((r) => {
            if (r instanceof RegExp) return r.test(p)
            if (typeof r === 'string') return r === p
            return false
          })
        )
      // The DEFAULT app always counts, and a matched app is ADDED to it.
      //
      // Not an exclusive pick: the shell renders on every page — its navbar,
      // sidebar and dialogs sit behind whatever app owns the route — so its
      // strings are needed everywhere. And route claims overlap by design:
      // canvas declares `/^\/[^/]+\/[^/]+/` for its `/owner/project` editor
      // URL, which also matches `/tasks/epics`, so an exclusive first-match
      // handed the tickets board the canvas namespace ALONE and left it
      // English inside a Georgian shell (measured live, 2026-09-02).
      //
      // Route matching still earns its keep: it adds the namespaces of an app
      // that genuinely claims this path, on top of the shell's own.
      // UNION over every candidate, not the first that hits. A greedy route
      // otherwise steals an intermediate reading: on
      // `/w/natali/<ws>/marketplace` the candidate `/<ws>/marketplace` matches
      // canvas's two-segment editor route, so a first-match loop stopped there
      // and the App Store — whose own manifest claims `/marketplace` on the
      // NEXT candidate — never contributed its namespace, and the store stayed
      // English (measured live, 2026-09-03). Route claims are not exclusive,
      // and loading one namespace too many costs a small chunk, while loading
      // one too few is a surface in the wrong language.
      const _matched = []
      for (const p of _candidates) {
        for (const m of _hits(p)) if (_matched.indexOf(m) === -1) _matched.push(m)
      }
      const _default = typeof defaultApp === 'string' ? (apps || {})[defaultApp] : defaultApp
      const _manifests = _default ? [_default, ..._matched.filter((m) => m !== _default)] : _matched
      for (const m of _manifests) {
        for (const ns of m.i18nNamespaces || []) {
          // a registered namespace has its English base bundle
          if (hasI18nBundle(DEFAULT_LANG, ns)) {
            _namespaces.add(ns)
            continue
          }
          // Dropped — no such bundle on disk. Say it, once.
          const _seen = (typeof window !== 'undefined' ? window : globalThis).__i18nNsWarned || {}
          const _mark = `${(m && m.key) || '?'}/${ns}`
          if (!_seen[_mark]) {
            _seen[_mark] = true
            ;(typeof window !== 'undefined' ? window : globalThis).__i18nNsWarned = _seen
            console.warn(
              `[bootShell] manifest '${(m && m.key) || '?'}' declares i18n namespace ` +
                `'${ns}', which the loader registry does not have — it is NOT loaded, ` +
                `so that surface stays English in every other language. Add ` +
                `packages/shared/i18n/en/${ns}.json (and the translated ones) and run ` +
                `node scripts/gen-i18n-registry.mjs, or declare a namespace that exists ` +
                `(packages/shared/i18n/en has the shipped set).`
            )
          }
        }
      }
      // The auth chrome renders for a visitor with no user at all, so it cannot
      // wait for a scope fetch to tell it which strings it needs. This is a
      // STATE condition (`no user resolved`), not a guess about a path — and
      // `GUEST_ROUTE_RE` is the shared constant the guest surfaces already
      // share, not a list maintained here.
      const _isGuestPath = _candidates.some((p) => GUEST_ROUTE_RE.test(p))
      if (!state?.user || _isGuestPath) _namespaces.add('auth')

      // ⚠️ THE PATH-PREFIX BRANCH IS GONE, ON PURPOSE.
      //
      // It read `/^\/(settings|profile|orgs|account)(\/|$)/` and added `account`
      // + `settings`. A hand-maintained regex over route prefixes was wrong
      // TWICE in one week: `/admin/…` does not match it, so a Georgian admin
      // read raw dotted keys (`settings.language.workspaceTitle`) across the
      // whole admin surface — which is why the new /admin/workspace-settings
      // page had to be written in plain English strings instead of keys. A
      // third route would have been wrong the same way, silently, because
      // English is bundled and resolves everywhere.
      //
      // A SURFACE DECLARES ITS NAMESPACES; NOTHING INFERS THEM FROM A URL.
      // There are exactly two declaration points now, and both are read above:
      //   • the app manifest's `i18nNamespaces` (packages/<app>/manifest.js) —
      //     the workspace manifest's own route list already covers `/admin/…`,
      //     so a page under it needs no separate declaration; and
      //   • `context.polyglot.namespaces` — the shared library's baseline
      //     (`common, auth, account, settings`), mirrored by `BOOT_NAMESPACES`
      //     in step 1b so it is present on BOTH mount paths.
      // A declared namespace that has no bundle now WARNS (above) instead of
      // vanishing. Admin pages have no manifest of their own and need none:
      // they are routes of the workspace-project app, whose manifest declares
      // them. A surface that ever DOES need its own set adds a manifest entry —
      // one declaration, in the package that owns the strings.
    } catch (err) {
      console.warn('[bootShell] i18n namespace routing failed:', err?.message || err)
    }
    return Array.from(_namespaces)
  }

  // FA204 / frank trap — self-contained inline copy of _isTokenRejection
  // (which itself inlines _causeChain), see the module-scope exports above
  // for the canonical shape kept alive for their own unit tests.
  function isTokenRejection(err) {
    function causeChain(err, maxDepth = 4) {
      const chain = []
      let cur = err
      for (let i = 0; cur && i < maxDepth; i++) {
        chain.push(cur)
        cur = cur.cause
      }
      return chain
    }
    if (!err) return false
    // A numeric status anywhere in the chain is the server's own answer: only
    // 401 (or the explicit `invalid_token` code) means the token was refused.
    // Any other status — a 500 whose message happens to read "No workspace…" /
    // "unauthorized" — is NOT a rejection, so the prose fallback below runs
    // only when no status exists at all (a session must never be purged on a
    // server error).
    let sawStatus = false
    for (const e of causeChain(err)) {
      const code = e?.code || e?.body?.code || e?.response?.body?.code
      if (code === 'invalid_token') return true
      const status = e?.status || e?.statusCode || e?.response?.status
      if (status === 401) return true
      if (typeof status === 'number') sawStatus = true
    }
    if (sawStatus) return false
    const s = String(err?.message || err || '')
    if (!s) return false
    return /\binvalid (?:or expired )?(?:access )?token\b|\btoken (?:signature is invalid|has expired|is malformed|has been revoked)\b|\bno bearer token\b|\binvalid signature\b|signature[_\s]+mismatch|\bjwt (?:malformed|expired)\b|missing\s+(?:workspace|user|active[\s_]?workspace)?[\s_]*claim|no\s+(?:workspace|active workspace)|\bunauthor(?:i[sz]ed)?\b|HTTP 401\b|Request failed:.*401/i.test(
      s
    )
  }

  // AUTH-WIPE-1 (c): on the LOCAL channel only, a signature-class 401 is
  // indistinguishable from a mis-restarted dev API verifying with the
  // wrong JWT secret (every token classifies as `signature_mismatch`).
  // Treat it as INFRA — the server could not JUDGE the token — and fall
  // through to the existing _getMeUnresolved soft path: no purge, no
  // redirect, no retry loop. Signature-class ONLY: expired / revoked /
  // malformed / invalid_token 401s purge on every host, and every
  // non-'local' channel (development, next, staging, production, and
  // unknown/null) purges for everything, unchanged — fail-closed.
  // FA204 / frank trap — self-contained inline copy of the module-scope
  // `_isDevInfraSignatureWindow` export above (kept alive for its own
  // unit tests).
  function isDevInfraSignatureWindow(err) {
    if (currentChannelFromHost() !== 'local') return false
    const reasons = []
    const codes = []
    const parts = []
    let cur = err
    for (let i = 0; cur && i < 4; i++) {
      reasons.push(String(cur.reason || cur.body?.reason || cur.response?.body?.reason || ''))
      codes.push(String(cur.code || cur.body?.code || cur.response?.body?.code || ''))
      parts.push(String(cur.message || cur.body?.message || ''))
      cur = cur.cause
    }
    // A KNOWN reason of any other class is decisive and fail-closed —
    // expired / revoked / malformed / missing still purge, even on local.
    // This is what makes "signature-class ONLY" true by CONSTRUCTION rather
    // than by prose matching.
    if (reasons.some((r) => r && r !== 'signature_mismatch')) return false
    if (reasons.includes('signature_mismatch')) return true
    if (codes.includes('invalid_signature')) return true
    // Prose fallback ONLY. Note `token signature is invalid` — the wording
    // authError.js actually sends. The bare `invalid signature` alternative
    // is jsonwebtoken's INTERNAL string, which the server consumes and
    // replaces; it is kept only for a raw/unenveloped path.
    return /\btoken signature is invalid\b|\binvalid signature\b|signature[_\s]+mismatch/i.test(
      parts.join(' ')
    )
  }

  // Pre-resolve the full auth snapshot used to seed the shell's root state
  // synchronously inside `onCreate` BEFORE `subscribeAuthSession` registers
  // its async listener. Without this seed, the shell mounts with schema
  // defaults (signedIn=false, jwtClaims=null, orgs=[]) and any consumer
  // gated on those values flashes between mount and the first auth fire —
  // NoOrgOverlay was the most visible case (empty "create team" modal
  // appearing for a frame before orgs landed). Computation mirrors the
  // merge + drift-filter logic in subscribeAuthSession so the post-mount
  // auth subscription's first INITIAL_SESSION fire is a no-op diff.
  function resolveAuthSnapshot(me, opts = {}) {
    if (!me) return null
    const mongoOrgs = Array.isArray(me.organizations) ? me.organizations : null
    const claims = { ...(me.app_metadata || {}) }
    const claimsHasOrgs = Array.isArray(claims.orgs) && claims.orgs.length > 0
    // Same fallback + drift-filter as subscribeAuthSession in
    // workspace/functions/auth.js (kept in sync — every change there
    // applies here too).
    if (!claimsHasOrgs && Array.isArray(mongoOrgs) && mongoOrgs.length > 0) {
      claims.orgs = mongoOrgs
    } else if (claimsHasOrgs && Array.isArray(mongoOrgs)) {
      const mongoIds = new Set(mongoOrgs.map((o) => String(o?.id || o?._id || '')).filter(Boolean))
      const filtered = claims.orgs.filter((o) => mongoIds.has(String(o?.id || o?._id || '')))
      // Backfill Mongo-only orgs the governance bridge stripped (personal
      // orgs are dropped from app_metadata.orgs at mint time). Mongo's
      // OrgMember is canonical so anything there is a real membership.
      // Matches the union in subscribeAuthSession — keep these two in sync.
      const jwtIds = new Set(filtered.map((o) => String(o?.id || o?._id || '')).filter(Boolean))
      const mongoOnly = mongoOrgs.filter((o) => !jwtIds.has(String(o?.id || o?._id || '')))
      claims.orgs = mongoOnly.length ? [...filtered, ...mongoOnly] : filtered
    }
    const orgsList = Array.isArray(claims.orgs) ? claims.orgs : []
    let workspacesAll = Array.isArray(claims.workspaces) ? claims.workspaces : []
    // URL-org pin (BOOT-RESHUFFLE F1). Mirror of auth.js resolveActiveOrgId's
    // MWT branch — "a tab's URL /w/<org-slug> PINS its org" — which this
    // snapshot never carried despite the kept-in-sync contract above. Without
    // it a cross-org /w/<slug> landing painted the PERSISTED org first and a
    // post-render switchOrg redid the whole boot in front of the user. The
    // caller passes the URL org's id only when it resolved to a MEMBER org,
    // plus that org's freshly-fetched workspace list (authoritative — the JWT
    // may carry none of them) so workspace/claims resolve INSIDE the URL org.
    const _urlPinRaw = opts.urlOrgId ? String(opts.urlOrgId) : null
    const urlPinOrgId =
      _urlPinRaw && orgsList.some((o) => String(o.id ?? o._id ?? '') === _urlPinRaw)
        ? _urlPinRaw
        : null
    if (urlPinOrgId && Array.isArray(opts.urlOrgWorkspaces) && opts.urlOrgWorkspaces.length) {
      const fetchedIds = new Set(opts.urlOrgWorkspaces.map((w) => String(w.id)))
      workspacesAll = [
        ...opts.urlOrgWorkspaces,
        ...workspacesAll.filter((w) => !fetchedIds.has(String(w.id)))
      ]
    }
    const activeWs = claims?.active_workspace_id || ''
    // List-shaped (JWT claims row — no `settings`). Stamped so downstream
    // `setActiveWorkspaceRecord` never mistakes it for a complete record; the
    // `_bootWsMatches` / eager-`getWorkspace` blocks below correct `snap.workspace`
    // to the full record when they resolve (see tickets/spec-workspace-record-shape.md).
    let workspace = stampWorkspaceShape(
      workspacesAll.find((w) => w.id === activeWs) || null,
      'list'
    )
    // Active-org resolution: URL pin wins (see above), then Mongo
    // source-of-truth (User.activeOrganization), then the app_metadata claims,
    // then orgs[0]. Validates membership against orgsList.
    const has = (id) => !!id && orgsList.some((o) => o.id === id)
    const mongoActiveOrgId = me.activeOrganization ? String(me.activeOrganization) : null
    const govActiveOrgId = workspace?.org_id || claims.workspace_org_id || null
    let activeOrgId = null
    if (urlPinOrgId) activeOrgId = urlPinOrgId
    else if (has(mongoActiveOrgId)) activeOrgId = mongoActiveOrgId
    else if (has(govActiveOrgId)) activeOrgId = govActiveOrgId
    // Last rung: the first org a list OFFERS, not orgs[0] — /auth/me lists
    // the empty personal org first (POB r5; ./visibleOrgs.js). Twin of
    // workspace auth.js resolveActiveOrgId: change both or neither.
    else activeOrgId = visibleOrgs(orgsList)[0]?.id || null
    if (activeOrgId) claims.workspace_org_id = activeOrgId
    const org = activeOrgId ? orgsList.find((o) => o.id === activeOrgId) || null : null
    // Cross-org workspace drift defense. `claims.active_workspace_id` and
    // `claims.workspaces` are JWT-scoped — neither is re-minted on every
    // `sdk.setActiveOrganization`. When the user switches orgs, Mongo's
    // `User.activeOrganization` updates first; the JWT lags until next refresh.
    // In that window `active_workspace_id` can point at a workspace IN THE
    // PREVIOUS ORG, and `claims.workspaces` can carry workspaces from every
    // org the user has ever touched. Surfacing either under the resolved
    // active org leaks the wrong org's data into every downstream consumer
    // that scopes by `workspace.id` — loadPrivateData, _activeWorkspaceProjects,
    // workspace switcher dropdowns, anything reading state.workspace.*.
    // Scope to the active org and re-resolve activeWorkspace against the
    // scoped list, falling back to its first entry if the JWT's pointer is
    // stale.
    const inActiveOrg = (w) =>
      !activeOrgId ||
      (w && String(w.org_id || w.organization_id || w.organization) === String(activeOrgId))
    const workspacesList = activeOrgId ? workspacesAll.filter(inActiveOrg) : workspacesAll
    if (workspace && !inActiveOrg(workspace)) {
      workspace = workspacesList[0] || null
      // ONE identity — mirror of the auth tick's drift correction (auth.js
      // nextClaims.active_workspace_id re-stamp): when the claim's workspace was
      // foreign-org and got re-picked in-org, the claim mirror MUST follow, or
      // every claim-reading consumer (fetch scope, dashboard-prefs pinning,
      // chat scoping) stays keyed to the OTHER org's workspace from first paint.
      if (workspace?.id) claims.active_workspace_id = String(workspace.id)
    }
    // localStorage.activeWorkspace is the canonical client pointer for the chosen
    // workspace. The JWT's `active_workspace_id` is NOT re-minted on an in-org
    // workspace switch — sdk.switchWorkspace only invalidates the claims cache and
    // writes localStorage — so after a switch + refresh the JWT (via getMe) still
    // names the PREVIOUS workspace. Without this, that stale-but-present claim wins
    // and the switch silently reverts on refresh. Prefer the LS pointer whenever
    // it resolves to a workspace in the active org's scoped list; otherwise keep
    // the JWT-resolved workspace and let the normalize block below backfill an
    // empty claim (the OAuth path, where the claim is genuinely absent).
    try {
      const lsActiveWs = getActiveWorkspacePointer() || ''
      if (lsActiveWs && String(lsActiveWs) !== String(claims.active_workspace_id)) {
        const lsWorkspace = workspacesList.find((w) => String(w.id) === String(lsActiveWs))
        if (lsWorkspace) {
          workspace = lsWorkspace
          claims.active_workspace_id = lsActiveWs
        }
      }
    } catch (_) {}
    // Normalize `active_workspace_id` across auth providers. The claim may be
    // absent from `app_metadata` (Google/OAuth sign-ins, fresh accounts), so
    // `activeWs` can be ''.
    // Downstream surfaces key off `jwtClaims.active_workspace_id` (home's
    // _wsScope dashboard cache, _activeWorkspaceProjects, loadPrivateData),
    // so when it's empty the home falls back to a DIFFERENT scope ('default'
    // vs the real ws id) and renders a different saved layout per provider
    // (#auth-provider-home-diff). Backfill from the resolved workspace, the
    // canonical localStorage pointer, or the first scoped workspace so both
    // providers resolve the SAME scope from first paint.
    if (!claims.active_workspace_id) {
      let lsActiveWs = ''
      try {
        lsActiveWs = getActiveWorkspacePointer() || ''
      } catch (_) {
        lsActiveWs = ''
      }
      // localStorage.activeWorkspace is the canonical client pointer (the JWT
      // claim goes stale on org switch — see org-switcher notes). Prefer a
      // resolved workspace, then the LS pointer (trusted even when the scoped
      // list hasn't hydrated yet, the OAuth worst case), then the first scoped
      // workspace.
      //
      // P0 tenant isolation (tickets/fable.md 2026-08-14): on a URL-PINNED
      // cross-org boot (`urlPinOrgId` set — the address bar names a different
      // member org than the pointer's), the un-validated pointer is KNOWN to
      // be suspect: it names the PREVIOUS tenant's workspace. Blind-trusting
      // it here stamped that foreign workspace id into
      // `claims.active_workspace_id` — the exact claim mirror every
      // downstream scope consumer (loadPrivateData, fetch scoping, dashboard
      // prefs) keys off — while `org` said the URL org. Trust the pointer on
      // a pinned boot only when it resolves INSIDE the pinned org's list;
      // otherwise leave the claim for the scoped list / auth tick to fill.
      const lsTrusted =
        !!lsActiveWs &&
        (!urlPinOrgId || workspacesList.some((w) => String(w.id) === String(lsActiveWs)))
      // CROSS-DEVICE POINTER (WS-ACTIVE-WS-NOT-PORTABLE-1) — `me.activeWorkspace`
      // is the SERVER-persisted choice on the User doc, written by
      // `sdk.switchWorkspace` -> `sdk.setActiveWorkspace` ->
      // `PATCH /auth/me/active-workspace` on every switch (that write is
      // unconditional and throws on failure, so the field is kept current).
      // It was already returned in the `/auth/me` payload and simply never
      // read here, so a browser with no `localStorage.activeWorkspace` — a
      // DIFFERENT ORIGIN (workspace.localhost vs dev.my.symbols.app), a
      // second machine, a new profile, a cleared cache — silently fell
      // through to `workspacesList[0]`, an ARBITRARY workspace. Measured:
      // a user whose list is [Company, personal, Symbols Intranet] landed in
      // Company (4 chat channels) instead of Symbols Intranet (67), which
      // reads to the user as "my channels are gone".
      //
      // Ordered AFTER the localStorage pointer deliberately: the local
      // pointer is this device's most recent explicit intent, so behaviour
      // is byte-identical whenever it is present. This only replaces the
      // arbitrary first-in-list fallback.
      //
      // ALWAYS validated against the scoped list — stricter than `lsTrusted`,
      // which only validates on a URL-pinned boot. A server id can name a
      // workspace whose membership was since revoked; an unvalidated one
      // would pin the boot to a workspace the user can no longer read.
      const serverActiveWs = String(me.activeWorkspace || '')
      const serverTrusted =
        !!serverActiveWs && workspacesList.some((w) => String(w.id) === String(serverActiveWs))
      const normalizedWs =
        (workspace && workspace.id) ||
        (lsTrusted ? lsActiveWs : '') ||
        (serverTrusted ? serverActiveWs : '') ||
        (workspacesList[0] && workspacesList[0].id) ||
        ''
      if (normalizedWs) {
        claims.active_workspace_id = normalizedWs
        if (!workspace)
          workspace = workspacesList.find((w) => String(w.id) === String(normalizedWs)) || workspace
      }
    }
    return {
      signedIn: true,
      jwtClaims: claims,
      orgs: orgsList,
      // List-shaped rows (JWT claims — no `settings`). See
      // tickets/spec-workspace-record-shape.md §4a.
      workspaces: stampWorkspaceListShape(workspacesList),
      org,
      workspace,
      currentUserEmail: me.email || '',
      currentUserId: me._id || me.id || '',
      currentUserUsername: me.username || '',
      // Legacy external auth UUID. Pre-resolving on the boot snapshot
      // means host-only gates that compare `room.created_by` against the
      // legacy id (meet rooms, canvas pages, migrated rows) match
      // correctly from the first paint — no flash where the host's own
      // SettingsBtn / EndRoomBtn render hidden because root state only
      // had the Mongo _id loaded.
      currentUserSupabaseId: me.supabaseUserId || '',
      // Demo-session flag. `getMe()` returns `isDemo: true` for sandbox
      // demo accounts (the server clears it once the user claims the
      // workspace via /demo/claim). Seeded here so DemoClaimBanner can
      // gate its visibility on `root.isDemo` from the first paint — the
      // banner is invisible to every real user and only appears for an
      // unclaimed demo session.
      isDemo: !!me.isDemo
    }
  }

  // Warm-start PAINT half (tickets/opus.md "boot: the scope-keyed warm-start
  // snapshot's PAINT half was never built"; storage half + full design:
  // packages/workspace/functions/bootSnapshot.js). Nika granted permission to
  // edit this file for this change, 2026-08-09 — conditions: test in a real
  // cold browser, clean the file while in here, re-measure (tickets/workspace.md
  // "boot: scope-keyed warm-start snapshot"). Scope of the grant is THIS
  // change, not a standing license on packages/shared/.
  //
  // Builds the SAME shape `resolveAuthSnapshot` returns, but from an
  // ALREADY-VALIDATED cached row (`warmSnapshot`, resolved by the caller via
  // bootSnapshot.js's `resolveWarmBootSnapshot()` — this function does no
  // scope judgment of its own; that lives in the package that owns the
  // isolation contract + its own test suite). Deliberately narrower than the
  // live snapshot: the cache is chrome-only (org, workspace, sibling lists,
  // current-user identity — see bootSnapshot.js's own "CONTENT" note), so
  // `currentUserSupabaseId` / `isDemo` default to their safe values here and
  // get corrected the moment the REAL `getMe()`/`boot()` response lands and
  // reconciles over this seed (see the warm-start block below, and the
  // reconcile at "8. Mount shell").
  //
  // `workspace` is stamped 'list'-shaped, NEVER 'full' — the cache never
  // stores `settings`, so treating it as complete would let an unconfirmed
  // guess paint as authoritative (the constraint `MAX_SNAPSHOT_ENTRIES`'s
  // sibling comments call out repeatedly). Stamping it 'list' means the
  // EXISTING full-record upgrade machinery (setActiveWorkspaceRecord, the
  // composite-boot / eager-getWorkspace branches a few hundred lines down)
  // treats this exactly like an ordinary claims-derived workspace mid-upgrade
  // — no new upgrade path needed, just the existing one doing its job.
  function authSnapshotFromCache(snap) {
    if (!snap || !snap.userId || !snap.orgId || !snap.workspaceId) return null
    const orgs = Array.isArray(snap.orgs) ? snap.orgs : []
    const workspaces = stampWorkspaceListShape(
      Array.isArray(snap.workspaces) ? snap.workspaces : []
    )
    return {
      signedIn: true,
      jwtClaims: {
        orgs,
        workspaces,
        active_workspace_id: String(snap.workspaceId),
        workspace_org_id: String(snap.orgId)
      },
      orgs,
      workspaces,
      org: snap.org || null,
      workspace: snap.workspace
        ? stampWorkspaceShape(normalizeWorkspaceRecord({ ...snap.workspace }), 'list')
        : null,
      currentUserEmail: snap.currentUserEmail || '',
      currentUserId: String(snap.userId),
      currentUserUsername: snap.currentUserUsername || '',
      currentUserSupabaseId: '',
      isDemo: false
    }
  }


  // 5. Theme mode — WS-THEME-BOOT-WRONG-MODE-1. Full mechanism + why the
  // pre-flight must stay bounded: themeMode.js (`resolveBootThemeMode`).
  // `let themeMode` above (not `const`) so this reassignment is the ONLY
  // place that needs to change: every later read of `themeMode` in this
  // function (both `globalTheme` seeds passed to smbls — the warm-start
  // early mount and the cold mount) picks up the correction automatically.
  //
  // ⚠️ This block sits HERE, after Pattern-B SDK instantiation, on purpose.
  // The workspace shell never passes `sdk` into bootShell — `sdk` is only
  // assigned a few lines up (`sdk = getSDK()`). The first version of this
  // pre-flight ran before that assignment, so `sdk` was `undefined` for the
  // exact consumer it was written for and `resolveBootThemeMode` returned
  // on its `!sdk` guard every time — dead code that shipped green
  // (measured on the deployed shell, 2026-08-19). The SDK proxy awaits its
  // own init before dispatching, so `sdk.execute` is safe this early; the
  // 300ms race bounds the whole thing. Pinned by
  // bootShell.themeBootWiring.test.js — keep it after `sdk = getSDK()`.
  const rawThemeModeAbsent = hasNoStoredThemeMode(storage) === true
  // ⚠️ SIGNED-OUT BOOT pays nothing. `/core/prefs/user` is an AUTHED endpoint,
  // and the /login screen has no session: measured on app.bellforge.eu
  // 2026-09-01, the pre-flight fired GET /core/prefs/user → 401 on the login
  // page, before any seat click, and then sat on the 300 ms race for an answer
  // that could never arrive. A visitor with no session also has no stored
  // preference to fetch, so the whole pre-flight is dead work there. Same
  // credential pair the auth snapshot reads further down (`_hasSdkToken`);
  // read defensively, because a storage-blocked context must boot, and a
  // false negative only costs the OS guess the timeout arm already returns.
  let _hasBootSession = false
  try {
    _hasBootSession =
      typeof window !== 'undefined' &&
      !!(
        window.localStorage.getItem('symbols_access_token') ||
        window.localStorage.getItem('symbols_bridge_access_token')
      )
  } catch (_) {
    _hasBootSession = false
  }
  const themeModeDecision = await resolveBootThemeMode({
    themeMode,
    rawKeyAbsent: rawThemeModeAbsent,
    sdk,
    signedIn: _hasBootSession
  })
  themeMode = themeModeDecision.mode
  if (themeModeDecision.applied) writeStoredThemeMode(themeMode)

  // 5b. TENANT DEFAULT THEME — "make bellforge workspace light by default,
  // overriding the user's system preference" (Nika); GreenIG "Dark theme by
  // default" (Nika 2026-10-18). Precedence, highest first: the user's OWN
  // choice → the workspace record's default → this tenant's brand default →
  // the OS.
  //
  // ⚠️ APPLIED AT THE BOOT SEED, deliberately NOT on the mounted app. The
  // first attempt lived in applyWorkspaceConfig and called
  // `changeGlobalTheme` after mount; it is parked there
  // (WORKSPACE_DEFAULT_THEME_ENABLED) because re-theming a mounted app
  // re-applies the design system across the whole tree. Seeding `themeMode`
  // here costs nothing — the value flows straight into the `globalTheme`
  // handed to smbls at create(), and into `themeResolved` just below, so
  // the FIRST paint is already correct and there is no flip to watch.
  //
  // ⚠️ Reads the BRAND and the workspace RECORD's own `settings.theme` —
  // never through `resolveWorkspaceConfig`: for a full-takeover client
  // module (bellforge, greenig) that returns early from
  // `{ ...DEFAULT_CONFIG, ...project.config }` and never reaches the branch
  // that carries `settings.theme`, so the record is read raw (the warm
  // snapshot's cached copy below, the full record at 7c). The brand is
  // already here: the workspace entry awaits
  // `seedWhitelabelBrand(state)` BEFORE calling bootShell, and the
  // branded login already treats `branding.theme` as the tenant's scheme —
  // so this makes the signed-in app agree with the login screen instead of
  // disagreeing with it.
  //
  // NEVER persisted — a tenant default is not a choice the user made, so
  // `writeStoredThemeMode` is not called here. Deciding again on every boot
  // is exactly what lets a later explicit user choice win permanently, and
  // what lets the default disappear if the tenant drops it.
  // Two sources, in this order, and the SECOND is the one that usually wins.
  //
  // `seedWhitelabelBrand` is bounded at 600ms so a slow lookup can never delay
  // the mount — which means on a slow resolve `state.whitelabelBrand`
  // is simply ABSENT here and the brand only lands later via
  // `applyWhitelabelBrandLate`. Measured on the deployed app: the surface
  // painted light (the module's own surfaces) while `data-theme` still read
  // `dark`, because this seed had nothing to read. The fix is not a longer
  // timeout — that would trade a wrong colour for a slower boot.
  //
  // So fall back to the host-keyed boot identity the brand resolver persisted
  // on the previous visit (whitelabelBrand.js `cacheWhitelabelBootIdentity`).
  // It is synchronous, needs no network, and is the same value index.html's
  // head script already used to stamp `data-theme` before first paint — so the
  // attribute and the framework's globalTheme now agree instead of disagreeing.
  // Normalised BEFORE the fallback: `brand.theme` can legitimately be a value
  // this seed cannot use (a theme NAME, or 'auto'), and a truthy-but-unusable
  // value would short-circuit the `||` and silently skip the cache.
  const seededBrandTheme =
    state && state.whitelabelBrand ? state.whitelabelBrand.theme : null
  const brandThemeMode =
    seededBrandTheme === 'light' || seededBrandTheme === 'dark'
      ? seededBrandTheme
      : readTenantBootThemeMode()
  const userChoseTheme = userHasOwnThemeChoice({
    rawStoredKeyAbsent: rawThemeModeAbsent,
    // `applied` means resolveBootThemeMode took the value off the USER
    // record, which is itself an explicit choice made on some device.
    userRecordMode: themeModeDecision.applied ? themeMode : null
  })
  // The user's / the OS's answer — the base every tenant layer falls back to,
  // kept for the cold seed (7c) to decide again from the full record.
  const themeBase = themeMode
  // THE WORKSPACE'S OWN DEFAULT (`workspace.settings.theme = { mode }` —
  // GreenIG's dark, Nika 2026-10-18) outranks the brand: the more specific
  // tenant layer. Here, for the WARM mount, from its snapshot (bootSnapshot.js
  // `workspaceTheme`, cached off the full record of this scope's last settled
  // boot); the cold mount decides again from the full record at 7c.
  themeMode = bootTenantThemeMode({
    baseMode: themeBase,
    userChoseExplicitly: userChoseTheme,
    workspaceMode: warmSnapshot ? warmSnapshot.workspaceTheme : null,
    brandMode: brandThemeMode
  })

  if (state) {
    state.themeMode = themeMode
    // Reactive RESOLUTION carrier (themeResolved.js, tickets/opus.md
    // "DECIDED: themeResolved") — `themeMode` above is the MODE, never the
    // resolved 'dark'|'light'. Seeds the schema default here (mirrors the
    // themeMode seed one line up); `watchThemeResolved` (wired on the
    // RESOLVED shell element, step 8 below) keeps it live for the rest of
    // the session, including 'auto' OS flips scratch's own listener applies
    // with no other trigger.
    if (typeof document !== 'undefined') {
      state.themeResolved = resolveThemeResolvedNow(document, themeMode)
    }
  }

  // 6. Auth gate — public auth routes must render to unauthed users
  // MUST await SDK init before reading service-proxied methods: the SDK
  // populates flat method proxies (sdk.getMe, sdk.login, …) inside
  // _createServiceProxies which only runs during initialize(). Without
  // this await, sdk.getMe() can be undefined → TypeError → bounce to
  // /login even though localStorage tokens are valid. (Cookies are
  // dropped on .localhost by Chrome — localStorage is the source of
  // truth in dev.)
  // `auth/magic-callback`, `auth/callback`, `github-auth-callback` all land
  // BEFORE the user has tokens in localStorage — the callback page is the
  // one that PERSISTS them. Treat as public so bootShell doesn't pre-empt
  // it with a /login redirect (which would also strip the URL hash that
  // carries the tokens).
  // `/user/:slug` and `/u/:slug` are public-facing community profile
  // pages — visitors browsing the marketplace feed land here when they
  // click an author. Bouncing to /login would break the shareable link.
  // `/marketplace` (+ subpaths) and `/feed` are publicly browsable —
  // anonymous visitors can view the catalog and editorial feed. The
  // Install / Use flow opens MarketplaceSigninModal (gated on
  // host.signinModal); a successful sign-in re-dispatches the pending
  // install via host._pendingInstall (see MarketplaceIsland's state
  // shape). Without that public access, the only way to discover the
  // marketplace before signup was a deep-link share — which then
  // redirected away before render.
  // meet-guest + _session-bridge are anonymous-by-design surfaces (the
  // waiting-room join form / the cross-app token hop). Without them here
  // bootShell's `!me` branch replaceState'd anonymous guests to /login —
  // the exact "guest link asks for signup" report (2026-06-10): the
  // route-level redirectIfUnauthed allowlists them, but this regex is
  // the SECOND gate and it bounced anyway.
  // Canonical guest-route allowlist (shared with workspace/app.js's SPA
  // route-change guard so the load-gate + nav-guard can't drift) — see
  // ./guestRoutes.js.
  const PUBLIC_AUTH_ROUTE = GUEST_ROUTE_RE

  // Warm-start PAINT half — the shell element mounted early from
  // `warmSnapshot`, if any (see the block below, right after
  // `_forceLogoutAndReauth`, for the actual attempt). Declared here (ahead
  // of that function) so it can dispose an abandoned early mount before
  // hard-redirecting — an early-mounted root left behind in `document.body`
  // when the REAL auth gate then bounces to /login would double-mount
  // alongside whatever the caller mounts next (workspace/index.js's own
  // `!shell` fallback).
  let _earlyShell = null
  const _disposeEarlyShell = () => {
    if (!_earlyShell) return
    try {
      _earlyShell.remove()
    } catch (err) {
      console.warn('[bootShell] failed to dispose abandoned warm-start shell:', err?.message || err)
    }
    _earlyShell = null
  }

  // Auth recovery: server-rejected tokens must be wiped before /login,
  // otherwise the next page boot re-uses the poisoned credential and the
  // user is trapped in a refresh-loop on /login. `_isTokenRejection` /
  // `_causeChain` are module-scope pure helpers (see above `bootShell`) —
  // hoisted out of this closure so they're independently unit-testable.

  // Hard-reload to /login after wiping sdk state + auth storage. Uses
  // the shared purgeAuthStorage() helper so the pre-mount logout path
  // here and the post-mount switchers/logout.js clear the same set of
  // keys (sb-*, symbols_*, sdk_only retry flag).
  //
  // BRIDGE PEER FIRST (2026-10-09): the server REJECTED this origin's token.
  // On a shell <-> canvas pair the session may be borrowed from the peer
  // (`/_session-bridge` hands over the ACCESS token only; the owner keeps the
  // refresh token and rotates it). Before purging and sending the user to
  // /login, ask the peer's bridge ONCE (sessionBridgeReacquire.js). The token
  // the server just refused is passed as `rejectedToken`: a reply with that
  // same token is no session, however fresh its local expiry looks. A new
  // token is written to storage and THIS tab reloads once, so the SDK re-inits
  // from it. The 60 s latch (`symbols_bridge_reask_at`, per tab) stops a
  // reload loop when the peer's token is rejected too.
  const _forceLogoutAndReauth = async (reason) => {
    context.bootSignedIn = false
    if (typeof window === 'undefined') return { aborted: true, navigated: true, signedIn: false }
    try {
      const _bk = 'symbols_bridge_reask_at'
      const _lastAsk = Number(window.sessionStorage.getItem(_bk) || 0)
      // ONLY a session adopted from the bridge asks it again (review M2): the
      // origin that owns its login (its own refresh token, no
      // `symbols_session_source: 'bridge'` marker) signs out when the server
      // refuses it — revoked, suspended, sign-out-everywhere — and never
      // borrows the peer's token.
      // Review D1 (Nika: "No, avoid the sign-out"): a refresh token without
      // the 'own' mark on the BORROWING origin (the canvas) is a copy an older
      // bridge build handed over — it asks the bridge too, instead of signing
      // the user out. A session with no refresh token AND no mark never asks
      // (review T1 / D4).
      const _src = window.localStorage.getItem('symbols_session_source')
      const _refresh = window.localStorage.getItem('symbols_refresh_token')
      const _adoptedFromBridge =
        (_src === 'bridge' && !_refresh) ||
        (!!_refresh &&
          _src !== 'own' &&
          typeof isSessionBridgeBorrower === 'function' &&
          isSessionBridgeBorrower())
      if (
        _adoptedFromBridge &&
        typeof reacquireBridgeSession === 'function' &&
        Date.now() - _lastAsk > 60000
      ) {
        window.sessionStorage.setItem(_bk, String(Date.now()))
        const _borrowed = await reacquireBridgeSession({
          rejectedToken: window.localStorage.getItem('symbols_access_token')
        })
        if (_borrowed) {
          console.warn(
            '[bootShell] token rejected; the session bridge handed a new one — reloading once'
          )
          _disposeEarlyShell()
          window.location.reload()
          return { aborted: true, navigated: true, signedIn: false }
        }
      }
    } catch (_) {
      /* storage or bridge unavailable — keep the purge path */
    }
    _disposeEarlyShell()
    try {
      sdk?.logout?.()
    } catch (_) {}
    purgeAuthStorage()
    // LOGINLOOP-1: was `encodeURIComponent(pathname + search)` — re-encoded
    // the WHOLE existing query verbatim, including any existing `next`,
    // nesting one level deeper on every bounce through this exact path
    // (byte-for-byte the reported URL). resolveAuthNext reuses an existing
    // next instead of re-wrapping it — see that file's own header for the
    // full mechanism and why it's not also wired into app.js in this pass.
    const dest = resolveAuthNext(window.location.pathname, window.location.search)
    const r = reason ? `${dest ? '&' : '?'}reason=${encodeURIComponent(reason)}` : ''
    window.location.replace(dest ? `/login?next=${encodeURIComponent(dest)}${r}` : `/login${r}`)
    return { aborted: true, navigated: true, signedIn: false }
  }

  // Warm-start PAINT half, the actual attempt (tickets/opus.md; design +
  // acceptance spec'd in tickets/workspace.md "boot: scope-keyed warm-start
  // snapshot"; storage half + the "is this snapshot safe for THIS URL"
  // judgment: packages/workspace/functions/bootSnapshot.js).
  //
  // `warmSnapshot` was resolved by the CALLER (workspace/index.js, via
  // bootSnapshot.js's `resolveWarmBootSnapshot()`) BEFORE this function was
  // even invoked — bootShell itself never touches localStorage or the URL
  // to decide whether a cached scope applies; it only ever trusts an
  // ALREADY-VALIDATED row. That keeps the isolation/scope-matching judgment
  // call inside the package that owns it (and its own test suite), not
  // duplicated here.
  //
  // When it validates, mount the shell IMMEDIATELY from the cached chrome —
  // strictly before the auth-gate's `sdk.boot()`/`getMe()` network round
  // trip a few lines down even starts. Every line of this function's
  // EXISTING auth-gate + eager scope-fetch logic below then runs completely
  // UNCHANGED, in the background relative to that already-painted shell —
  // this is deliberately NOT a second/parallel boot path (that is exactly
  // how the `/w/<org>` boot-reshuffle bug happened the first time: two
  // uncoordinated things deciding scope). Step 8 ("Mount shell", a few
  // hundred lines down) detects the early mount and reconciles the live
  // root with whatever that unchanged logic actually resolved to, instead
  // of mounting a second time. Cold boots / first-ever visits / explicit
  // tenant switches are unaffected — `resolveWarmBootSnapshot()` returns
  // null on all of those (no entry exists yet, or the switch's PHASE-1
  // purge already removed the outgoing one — see bootSnapshot.js) — so
  // every caller that doesn't resolve a warm snapshot, and every caller
  // that never passes this option at all (preview/canvas/marketplace/
  // analytics — the other 4 apps this shared file boots), is completely
  // unaffected: byte-identical to today.
  // A white-label host whose tenant could not be established paints NO cached
  // chrome: the cached row is keyed by a pointer the host never confirmed, so
  // its roster and records may be another tenant's (WHITELABEL-STRICT-TENANT-
  // SCOPE-1). The live answer below still boots — into an empty tenant.
  if (
    warmSnapshot &&
    mountEarly &&
    !publicSurface &&
    sdk &&
    state &&
    whitelabelHostScopeOf() !== false
  ) {
    const _cachedAuth = authSnapshotFromCache(warmSnapshot)
    if (_cachedAuth) {
      try {
        if (!context.methods) context.methods = {}
        if (typeof context.methods !== 'object') context.methods = {}
        context.methods.sdk = sdk
        context.preResolvedAuth = scopeSnapshotToWhitelabelHost(
          _cachedAuth,
          whitelabelHostScopeOf()
        )
        // Roster — the ticket's own 4th named chrome field ("member
        // roster"), alongside org/workspace/identity which preResolvedAuth
        // already covers. `loadPrivateData`'s own early kick (fired by
        // onCreate's existing `_preAuth` branch below, unchanged) refreshes
        // this from a real fetch moments later regardless.
        state.peopleAll = Array.isArray(warmSnapshot.members) ? warmSnapshot.members : []
        // NEW flag — deliberately NOT `workspaceReady` (that means
        // "confirmed complete record"; overloading it would let an
        // unconfirmed cache paint as authoritative). Declared in state.js
        // so smbls 3.14's typed-bag `state.update()` doesn't drop it on the
        // reconcile write in step 8.
        state._bootFromCache = true
        // FIRST PAINT IN THE WORKSPACE'S LANGUAGE — the strings too, not only
        // `lang`. Step 1 resolved `lang` from the snapshot's cached language
        // pair; `prepareState` seeds the active language's keys into the root
        // state DURING the mount, so a non-English `lang` over an English-only
        // `translations` would paint English strings under a Georgian `lang`.
        // Load the session's WHOLE text set (`sessionI18nNamespaces`, the set
        // step 7b declares), with 7b's collision rule, BEFORE the mount. It
        // used to load bootPrepare's baseline only: the shell's other sets
        // (canvas, chat, mail, …) painted English under a Georgian `lang`
        // until 7b + step 8 redelivered — the composer's "Thinking…" read
        // English for ~0.4 s after every reload (KA-1 race, 2026-10-30). 7b
        // and 8b still run, in the SAME language: cache hits, no second
        // language paint.
        if (lang !== DEFAULT_LANG) {
          try {
            const _earlyNs = sessionI18nNamespaces()
            const _earlyMerged = await loadI18nNamespaces(lang, _earlyNs)
            if (Object.keys(_earlyMerged).length) {
              const _earlyExisting = _poly.translations[lang] || {}
              _poly.translations[lang] = _earlyNs.some((ns) => hasI18nBundle(lang, ns))
                ? { ..._earlyExisting, ..._earlyMerged }
                : { ..._earlyMerged, ..._earlyExisting }
            }
          } catch (err) {
            console.warn(
              '[bootShell] early i18n load for the warm mount failed:',
              err?.message || err
            )
          }
        }
        // The early mount itself is the composer's (parcel bootShell): a boot step
        // never calls create().
        _earlyShell = await mountEarly(themeMode)
      } catch (err) {
        console.warn(
          '[bootShell] warm-start early mount failed, falling back to the cold path:',
          err?.message || err
        )
        _disposeEarlyShell()
        // The code below computes context.preResolvedAuth fresh from the
        // REAL getMe()/boot() response regardless of this branch — but
        // peopleAll/_bootFromCache were only ever meant for the (now
        // abandoned) early element, and must not leak into whatever mounts
        // next.
        state.peopleAll = []
        state._bootFromCache = false
        context.preResolvedAuth = null
      }
    }
  }

  // Sections from the composite /core/boot payload (set only when the
  // endpoint exists and answered inside the auth gate below) — consumed by
  // the scope-fetch stage AFTER the auth gate's block closes, so it MUST
  // live at this function scope, not inside `if (sdk)`.
  let _bootSections = null

  if (sdk) {
    // BOOT DEADLINE: SDK init can refresh the stored session over the network.
    // An init that never settles ends in the loader's error + reload state
    // (a public surface carries on signed out, as for any failed session).
    try {
      await withBootDeadline(() => awaitSDKInit(), callDeadlineMs, 'sdk-init')
    } catch (initErr) {
      if (!isBootDeadline(initErr)) throw initErr
      console.warn('[bootShell] ' + initErr.message)
      if (!publicSurface) {
        context.bootSignedIn = false
        context.bootFailed = 'deadline'
        publishBootFailure('sdk_init_deadline')
        _disposeEarlyShell()
        return { aborted: true, failed: 'deadline', signedIn: false }
      }
    }
    // Attach SDK to context.methods AFTER init so DOMQL exposes el.sdk only
    // when service proxies (sdk.getMe, sdk.login, …) are populated. Reactive
    // factories that read el.sdk on first render won't see a half-built proxy.
    if (!context.methods) context.methods = {}
    if (typeof context.methods !== 'object') context.methods = {}
    context.methods.sdk = sdk

    // Wire realtime provider — SDK exposes a transport-agnostic
    // `workspaceProject.realtime.*` API; the provider plugs in server SSE +
    // the SDK user-socket as the actual wire (see sdkRealtimeProvider.js —
    // the transport it replaced was retired with the
    // governance project).
    // `sdk` is the init-await proxy (sdkClient.js wrapWithInitAwait), so
    // getService answers a PROMISE. Read synchronously, `wp` was that promise,
    // the guard was false and the provider was never installed: every meet,
    // presence and notifications subscription was a silent no-op (no live
    // captions, no live transcript panel, no spoken Fly asks — measured on
    // dev 2026-10-23). Init has settled here, so the await costs a microtask.
    try {
      const wp = await sdk.getService?.('workspaceProject')
      if (wp && typeof wp.setRealtimeProvider === 'function') {
        wp.setRealtimeProvider(realtimeProvider)
      } else {
        console.warn(
          '[bootShell] realtime provider not wired — no workspaceProject.setRealtimeProvider'
        )
      }
    } catch (err) {
      console.warn('[bootShell] could not wire realtime provider:', err?.message || err)
    }

    // Auth diagnostic — one snapshot on boot so the cause of any /workspace/… route
    // 401s is obvious in the console without paste-and-decode gymnastics.
    // Also exposed as window.__auth.diagnose() for on-demand re-checks.
    try {
      const dx = diagnoseAuth()
      if (typeof window !== 'undefined') {
        window.__auth = window.__auth || {}
        window.__auth.diagnose = runAuthDiagnostic
        window.__auth.snapshot = dx
      }
      // `all_missing` on cold load is a false positive on public auth routes
      // (login, signin, signup, reset-password) — the user is intentionally
      // unauthenticated there and no tokens is the correct, expected state.
      // On protected routes, token hydration (cookie/iframe handoff) lands
      // asynchronously after bootstrap, so defer and re-check before logging.
      // The `sdk_only` diagnosis is logged immediately on any route. (#2263)
      const _isPublicAuthPath =
        typeof window !== 'undefined' &&
        /^\/(login|signin|signup|signup-confirmation|reset-password|forgot-password|verify-email|auth\/magic-callback|auth\/callback|github-auth-callback)(\/|\?|#|$)/.test(
          window.location.pathname
        )
      if (dx.diagnosis === 'all_missing') {
        if (!_isPublicAuthPath) {
          setTimeout(() => {
            const dx2 = diagnoseAuth()
            if (typeof window !== 'undefined') window.__auth.snapshot = dx2
            if (dx2.diagnosis !== 'ok' && dx2.diagnosis !== 'all_missing') {
              console.warn(`[bootShell] auth diagnostic: ${dx2.diagnosis} — ${dx2.suggestedAction}`)
            } else if (dx2.diagnosis === 'all_missing') {
              console.warn(`[bootShell] auth diagnostic: ${dx2.diagnosis} — ${dx2.suggestedAction}`)
            }
          }, 250)
        }
      } else if (dx.diagnosis !== 'ok') {
        console.warn(`[bootShell] auth diagnostic: ${dx.diagnosis} — ${dx.suggestedAction}`)
      }
    } catch (err) {
      console.warn('[bootShell] auth diagnostic failed:', err?.message || err)
    }
    // SDK proxies service methods directly onto the SDK instance via
    // _createServiceProxies — there is no `sdk.auth` namespace. AuthService
    // method `getMe` is exposed as flat `sdk.getMe()`. (See
    // sdk/src/utils/services.js SERVICE_METHODS map: getMe → 'auth'.)
    //
    // Skip the network round-trip when no token exists in localStorage —
    // we already know the answer is "not authed", and firing the request
    // surfaces a misleading 401 (Unauthorized) on the landing page for
    // logged-out users.
    // Named `_me` (not `me`) — a bare `me` binding here collides with
    // Parcel/SWC's scope-hoisting renamer (see tickets/workspace.md
    // "bootShell: `me is not defined`"): the renamer correctly rewrote
    // the declaration + most reads to `me1` but silently dropped the
    // rewrite for the two references inside the nested
    // try→if→if(crossOrg)→try/catch block (the URL-org pre-render-switch
    // path), leaving a bare `me` identifier in the emitted bundle that
    // throws `ReferenceError: me is not defined` at runtime — 100%
    // reproducible on a cold deep-link that lands on a URL naming a
    // DIFFERENT org than the user's persisted `activeOrganization`. A
    // more unique local name sidesteps the collision entirely.
    let _me = null
    // Set whenever getMe() failed WITHOUT `_isTokenRejection` positively
    // identifying the failure as a server-confirmed bad token — a raw
    // transport failure (TypeError, server unreachable), an HTTP 5xx (dev
    // nodemon restart window, a rolling deploy's brief gap), a timeout, or
    // any other error shape. The `!_me` fallthrough must NOT wipe the
    // credential in that case (see the note in the catch block below).
    // Named `_getMeUnresolved` rather than `_getMeNetworkFailed`: it used to
    // be set ONLY for a narrow network-TypeError regex match, which meant
    // every OTHER kind of ambiguous failure (5xx, unexpected error shapes)
    // fell through to the destructive force-logout path by DEFAULT — the
    // actual mechanism behind the hard-reload session-drop bug (#724, see
    // tickets/workspace.md). The safe default is now "preserve the
    // credential unless the rejection is proven", not the reverse.
    let _getMeUnresolved = false
    // AUTH-WIPE-1 (c): carries the dev-infra verdict from the catch block
    // down to the soft bounce, so the visible stop can name the real cause.
    let _devInfraSignature = false
    // BOOT DEADLINE: the session check itself never answered (bootDeadline.js).
    let _gateDeadline = false
    const _hasSdkToken =
      typeof window !== 'undefined' &&
      !!(
        window.localStorage.getItem('symbols_access_token') ||
        window.localStorage.getItem('symbols_bridge_access_token')
      )
    if (_hasSdkToken) {
      try {
        // `sdk.getMe()` returns the server's `response.data` shape
        // (`{ user: {...} }`), NOT the user object directly. Unwrap so
        // downstream code (`_me.email`, `_me._id`, `_me.organizations`,
        // `_me.activeOrganization` — all read by `_resolveAuthSnapshot`
        // and the eager scope fetch) sees the user fields at the top
        // level. Without this unwrap, `resolveAuthSnapshot(_me)` reads
        // every field as undefined → preResolvedAuth has empty
        // `currentUserEmail` → WeekView's filter
        // `ev.googleCalendarId === currentUserEmail` rejects every
        // event → calendar renders 0 cards despite the underlying
        // table holding hundreds of rows. (Repro: /calendar on a
        // freshly-boot tab; SDK execute returned 139 events, page
        // showed none.)
        // Composite boot (one round trip, server d50d003b): /core/boot
        // returns me + org + workspaces + workspace + members + prefs
        // together. `me` keeps getMe's exact semantics (byte-equivalent
        // payload, same auth middleware, same rejection contract); the other
        // sections replace the eager scope fetches below and prime
        // loadPrivateData via context.bootPayload. Feature-detected — older
        // SDKs/servers fall back to plain getMe(); any null section falls
        // back to its individual fetch at its consumption site.
        // Snapshot the stored access token BEFORE the gate's network round
        // trip — the fuel-cut check in the rejection branch below compares
        // against it to detect a cross-tab rotation that landed mid-flight.
        let _gateStartAccessToken = null
        try {
          _gateStartAccessToken = window.localStorage.getItem('symbols_access_token')
        } catch (_) {}
        if (typeof sdk.boot === 'function') {
          try {
            const bootRes = await withBootDeadline(
              () => sdk.boot({ workspaceId: getActiveWorkspacePointer() || undefined }),
              callDeadlineMs,
              'boot'
            )
            const bd = bootRes?.data || bootRes || null
            if (bd && (bd.me || bd.org || bd.workspaces)) {
              _bootSections = bd
              _me = bd.me?.user || bd.me || null
              // Prime the shared record cache with every COMPLETE read the boot
              // carries, BEFORE anything can ask for one - the eager scope read
              // below and the first auth tick's list-row upgrade
              // (setActiveWorkspaceRecord) then make no request. Complete =
              // carries the caller's `access`: the server adds it only on a
              // single workspace read (WorkspaceController.getWorkspace ->
              // callerAccess), so the `workspace` section has it and a
              // `workspaces` list row never does (same projection otherwise).
              // A row primed as THE record was served back as a full record
              // without `access` for the cache's TTL - moduleRole unknown on
              // every later adoption. Unprimed, a later reader joins the one
              // read step 7 makes for it ("the caller's access").
              if (typeof primeWorkspaceCache === 'function') {
                const _rows = [
                  ...((bd.workspaces && (bd.workspaces.items || (Array.isArray(bd.workspaces) ? bd.workspaces : []))) || []),
                  ...(bd.workspace ? [bd.workspace] : [])
                ]
                for (const w of _rows) {
                  const id = w && (w._id ?? w.id)
                  if (id && w.access && typeof w.access === 'object') {
                    try {
                      primeWorkspaceCache(String(id), w)
                    } catch (_) {}
                  }
                }
              }
            }
          } catch (bootErr) {
            // Same disambiguation contract as getMe: a server-proven token
            // rejection logs out here; anything else falls through to the
            // legacy getMe below, which owns the unresolved semantics.
            // AUTH-WIPE-1 (c): a dev-host signature-class 401 skips this
            // branch too — it falls through to the getMe retry below,
            // which fails the same way and lands in getMe's own catch,
            // where the soft (non-destructive) path already lives. One
            // soft path, not two — see isDevInfraSignatureWindow above.
            if (
              !publicSurface &&
              isTokenRejection(bootErr) &&
              !isDevInfraSignatureWindow(bootErr) &&
              !PUBLIC_AUTH_ROUTE.test(window.location.pathname)
            ) {
              // LOGIN-BOUNCE FUEL CUT (2026-09-02, Nika "sometimes still
              // goes 'Aw, snap'"): the rejection this branch sees is very
              // often the CROSS-TAB ROTATION RACE, not a dead session —
              // another same-origin tab consumed the single-use refresh
              // first and has ALREADY re-written fresh tokens to
              // localStorage by the time this rejection lands. Purging +
              // bouncing here is what fuels the login<->app loop the
              // /login circuit breaker exists for (each cycle re-evaluates
              // the full dev bundle twice; enough cycles OOM the renderer).
              // So: if the stored access token CHANGED since this boot's
              // gate started, a healthy rotation happened elsewhere —
              // reload THIS tab once so the SDK re-inits from the fresh
              // pair, with a 60s one-shot latch so a genuinely dead
              // session (tokens unchanged, or the retry fails the same
              // way) still falls through to the purge+reauth below.
              try {
                const _freshTok = window.localStorage.getItem('symbols_access_token')
                const _rk = 'symbols_boot_retry_at'
                const _last = Number(window.sessionStorage.getItem(_rk) || 0)
                if (
                  _freshTok &&
                  _freshTok !== _gateStartAccessToken &&
                  Date.now() - _last > 60000
                ) {
                  window.sessionStorage.setItem(_rk, String(Date.now()))
                  console.warn(
                    '[bootShell] token rejected but another tab rotated a fresh pair — reloading once instead of purging'
                  )
                  window.location.reload()
                  return { aborted: true, navigated: true }
                }
              } catch (_) {
                /* storage unavailable — keep the purge path */
              }
              console.warn(
                '[bootShell] auth gate boot() rejected token; forcing logout:',
                String(bootErr?.message || '')
              )
              return _forceLogoutAndReauth('session_invalid')
            }
            console.warn(
              '[bootShell] composite boot failed; falling back to getMe:',
              bootErr?.message || bootErr
            )
          }
        }
        if (!_me) {
          const raw = await withBootDeadline(() => sdk.getMe(), callDeadlineMs, 'getMe')
          _me = raw?.user || raw || null
        }
      } catch (e) {
        const errMsg = String(e?.message || '')
        // Server-rejected token → wipe + hard reload so the same poisoned
        // credential doesn't re-fail on the next boot.
        // AUTH-WIPE-1 (c): EXCEPT on the local dev channel, where a
        // signature-class 401 is infra (mis-restarted API, wrong JWT
        // secret), not a proven-dead credential — see
        // isDevInfraSignatureWindow above. Every other channel is
        // unaffected: isDevInfraSignatureWindow always returns false off
        // the 'local' channel, so this condition is byte-identical to
        // today there.
        if (
          !publicSurface &&
          isTokenRejection(e) &&
          !isDevInfraSignatureWindow(e) &&
          !PUBLIC_AUTH_ROUTE.test(window.location.pathname)
        ) {
          console.warn('[bootShell] auth gate getMe rejected token; forcing logout:', errMsg)
          return _forceLogoutAndReauth('session_invalid')
        }
        // Anything that reaches this line was NOT a proven rejection —
        // `_isTokenRejection` already early-returned above for the
        // server-confirmed case. This covers a raw transport failure
        // (TypeError — server mid-restart under the dev nodemon window
        // after every server commit, VPN blip, DNS), an HTTP 5xx (a
        // rolling-deploy gap, a dev proxy answering while the backend is
        // still coming up), a timeout, or any other error shape this
        // classifier doesn't recognize. The token was never actually judged
        // by the server, so don't let the `!_me` fallthrough below wipe it —
        // bounce softly (if at all) and let the stored credential sign the
        // user straight back in on the next boot. This was the root cause
        // of both the "every rebuild logs me out" cycle (2026-06-10) and
        // the intermittent hard-reload session-drop (#724): an unrecognized
        // error used to fall through to the SAME forced-logout branch as a
        // genuine rejection, defaulting "ambiguous" to "guilty" instead of
        // "innocent".
        _getMeUnresolved = true
        if (isBootDeadline(e)) _gateDeadline = true
        // AUTH-WIPE-1 (c): name the dev-infra case explicitly so the
        // console reads as an actionable stop, not a silent swallow — the
        // credential is being KEPT (not purged), and the fix is an
        // operational restart, not a re-login. Unconditional on
        // publicSurface: isDevInfraSignatureWindow only ever returns true
        // on the 'local' channel, which every preview/canvas/workspace
        // consumer can hit alike.
        if (isDevInfraSignatureWindow(e)) {
          _devInfraSignature = true
          console.warn(
            '[bootShell] dev API JWT-secret mismatch (signature-class 401 on the local ' +
              'channel) — auth storage KEPT. Restart the dev API via ~/www/deploy/bin/dev.sh ' +
              '(or with the infisical env), then reload.'
          )
        }
        // publicSurface (preview): visitors are usually anonymous or
        // carrying a stale cross-env token; getMe failing is expected and
        // the `!_me && publicSurface` branch below handles it. WARN level
        // is reserved for the shell where a failure is actionable.
        if (publicSurface) {
          console.debug('[bootShell] preview auth gate getMe skipped:', errMsg)
        } else {
          console.warn('[bootShell] auth gate getMe failed:', errMsg)
        }
      }
    }
    // THE GATE'S VERDICT. A stored token is not a session (it can be expired
    // or revoked): only a user the server returned here signs the shell in.
    // Every "signed in" decision reads this (context.bootSignedIn, the
    // `signedIn` of the result), never the raw token; a decision made before
    // it exists waits for context.bootReady.
    context.bootSignedIn = !!_me
    if (!_me) {
      // `publicSurface: true` opts out of the auth bounce — used by the
      // preview app, which serves visibility-gated PUBLIC snapshots and
      // must render for logged-out users. The fetch path inside preview
      // surfaces its own "not public" UI when the project gate denies.
      if (publicSurface) {
        if (state) state.user = null
        // Fall through to the rest of bootShell so the shell mounts;
        // skip the eager scope fetch since there's no user.
      } else {
        // _hasSdkToken means we tried with a stored credential and got
        // no user back — wipe before /login so the next attempt is
        // clean. Without a token there's nothing to clear; soft
        // replaceState is fine. EXCEPTION: when getMe() failed WITHOUT a
        // proven rejection (`_getMeUnresolved` — transport failure, 5xx,
        // timeout, or any other ambiguous shape — see the catch block
        // above), the credential was never actually rejected — keep it and
        // bounce softly so the next boot signs straight back in instead of
        // forcing a re-login.
        if (
          _hasSdkToken &&
          !_getMeUnresolved &&
          !PUBLIC_AUTH_ROUTE.test(window.location.pathname)
        ) {
          return _forceLogoutAndReauth('session_invalid')
        }
        // BOOT DEADLINE: the session check never answered. Nobody judged the
        // stored session, so it is KEPT and the page is NOT sent to /login (a
        // signed-in user would only bounce back and wait again). The loader
        // shows its error line + reload button instead; the composer and the
        // published root mount nothing (context.bootFailed).
        if (_gateDeadline && !PUBLIC_AUTH_ROUTE.test(window.location.pathname)) {
          console.warn('[bootShell] the session check did not answer — boot stopped, reload offered')
          context.bootFailed = 'deadline'
          publishBootFailure('session_deadline')
          _disposeEarlyShell()
          return { aborted: true, failed: 'deadline', signedIn: false }
        }
        // DECLARE THE SESSION'S TEXT SETS — declare only, load nothing. This
        // exit returns before step 7b, the place that declares them on every
        // other boot, so the next `setLang` here (the workspace language
        // policy after an in-page sign-in) loaded bootPrepare's baseline only.
        // Read BEFORE the bounce below rewrites the URL (see
        // sessionI18nNamespaces). The login page's own text (`common` +
        // `auth`) still loads in applyPublicBootLang below, and nothing else
        // loads here. A language switch ON the login page now loads this set
        // too: the session the visitor signs in to, as on the signed-in path.
        if (_poly) _poly.namespaces = sessionI18nNamespaces()
        if (!PUBLIC_AUTH_ROUTE.test(window.location.pathname)) {
          // Preserve the inbound destination (path + query) as `?next=` —
          // same convention as _forceLogoutAndReauth above. A bare
          // replaceState('/login') silently discarded every deep link,
          // invitation ?token= links above all (P0 invite-flow fix,
          // 2026-08-06). Bare `/` stays a plain /login.
          // LOGINLOOP-RELEASE-1: was `pathname + search` verbatim — the
          // FOURTH bounce site, and the one that fires when a stale SDK
          // token is present and /auth/me fails (measured on production
          // 2026-08-27). resolveAuthNext reuses an existing `next` instead
          // of re-wrapping it, and returns null for bare `/`.
          const dest = resolveAuthNext(window.location.pathname, window.location.search)
          const params = new URLSearchParams()
          // KA-1 — an explicit `?lang=` on the inbound link survives the
          // bounce as /login's own `lang`, so the login renders in it and a
          // reload of /login keeps it. Shipped codes only, region dropped
          // (the same reading applyPublicBootLang makes; read here because
          // this boot step has no element to el.call through yet).
          const inboundLang = String(
            new URLSearchParams(window.location.search).get('lang') || ''
          )
            .trim()
            .toLowerCase()
            .split(/[-_]/)[0]
          if (['en', 'ka'].indexOf(inboundLang) !== -1) params.set('lang', inboundLang)
          if (dest) params.set('next', dest)
          // AUTH-WIPE-1 (c) — Nika's third condition, the VISIBLE STOP. Without
          // this the dev-infra case is indistinguishable from an ordinary
          // sign-out: storage is kept, but the user still lands on /login with
          // no explanation and re-enters credentials that were never the
          // problem. Naming it in the URL lets the login surface say the dev
          // API is misconfigured and that a restart — not a re-login — is the
          // fix. Only ever set on the 'local' channel (see
          // isDevInfraSignatureWindow); every other channel is unchanged.
          if (_devInfraSignature) params.set('authInfra', 'dev_signature')
          const qs = params.toString()
          window.history.replaceState(null, '', `/login${qs ? `?${qs}` : ''}`)
        }
        // This is a SOFT bounce (replaceState, no reload) — unlike
        // _forceLogoutAndReauth's hard `window.location.replace`, execution
        // continues on THIS page after returning. An early-mounted warm
        // shell left behind here would double-mount alongside whatever the
        // caller mounts next for the null return (workspace/index.js's own
        // `!shell` fallback creates its own fresh root).
        // WHITELABEL-BRAND-LANG-2 — the branded LOGIN is this exit. Step 7b
        // never runs on the bounce, so the auth shell would mount with first
        // paint's `stored || en`, and `branding.lang` would reach the marks
        // but never the copy. Resolve, load and stamp it here (see
        // publicBootLang.js).
        // KA-1 (2026-10-25) — EVERY signed-out exit, not only a branded host:
        // the platform's own login read no signal at all, so a Georgian
        // browser got English. `?lang=` → brand → browser → English.
        try {
          await applyPublicBootLang({ context })
        } catch (err) {
          console.warn('[bootShell] signed-out page language failed:', err?.message || err)
        }
        _disposeEarlyShell()
        return { aborted: true, signedIn: false }
      }
    } else if (state) {
      state.user = _me
    }

    // Stash the full pre-resolved auth snapshot on context so the
    // shell's onCreate can synchronously seed `s.root` BEFORE the
    // async `subscribeAuthSession` listener registers. This eliminates
    // the empty-state flash window where reactive consumers (e.g.
    // NoOrgOverlay) evaluated against schema defaults (signedIn=false
    // → orgs=[]) for the frame between mount and the first auth fire.
    // Null when no user is resolved (publicSurface or anonymous boot).
    // WS-WHITELABEL-TENANT-SCOPE-1 — a branded host sees ONE tenant.
    //
    // Applied where the snapshot is ADOPTED, never inside the builders. Both
    // builders' `return { signedIn: true, … }` literals are a CLOSED,
    // classified shape that __tests__/bootAuthSnapshotClosedShape.test.js
    // anchors on textually; scoping inside one would have made that guard
    // silently measure a single builder instead of two. Wrapping every
    // adoption site instead keeps the literals byte-identical, adds no key,
    // and covers the WARM-START builder on the same line of defence — a warm
    // boot must not render the wrong tenant either.
    //
    // The scope is `state.whitelabelHostScope` (whitelabelHostScopeOf above),
    // already awaited by seedWhitelabelHostScope before bootShell runs
    // (packages/workspace/index.js): no new fetch, no new await, nothing to
    // race. A platform host has no scope, so the snapshot is returned BY
    // IDENTITY.
    //
    // WHITELABEL-STRICT-TENANT-SCOPE-1 — and BEFORE the snapshot is built, the
    // host names this boot's org and workspace. `me.activeOrganization` /
    // `me.activeWorkspace` are the inputs every rung below resolves from (the
    // snapshot's active-org pick, step 7's eager org + workspace fetch), so
    // stating the host's tenant there makes the existing ladder resolve
    // INSIDE it, instead of resolving the account's stored choice (the org
    // last used on my.symbols — measured: Keepz on unreal.pr.symbols.app) and
    // narrowing afterwards. In memory, for this boot, never written back: the
    // stored choice belongs to the platform host. Only for a MEMBER of the
    // host's org; a non-member keeps their record and the scoping hands them
    // an empty tenant.
    const _bootHostScope = whitelabelHostScopeOf()
    if (_me && _bootHostScope && _bootHostScope.organizationId) {
      const _hostOrgId = String(_bootHostScope.organizationId)
      const _memberRows = [
        ...(Array.isArray(_me.organizations) ? _me.organizations : []),
        ...(Array.isArray(_me.app_metadata && _me.app_metadata.orgs) ? _me.app_metadata.orgs : [])
      ]
      const _isHostMember = _memberRows.some(
        (o) => String((o && typeof o === 'object' ? o.id || o._id : o) || '') === _hostOrgId
      )
      if (_isHostMember) {
        _me.activeOrganization = _hostOrgId
        if (_bootHostScope.workspaceId) _me.activeWorkspace = String(_bootHostScope.workspaceId)
      }
    }
    context.preResolvedAuth = scopeSnapshotToWhitelabelHost(
      resolveAuthSnapshot(_me),
      _bootHostScope
    )

    // Warm-start: the live getMe()/boot() answer just landed (signed-in or
    // not) — clear the cache flag HERE, not only in the "8. Mount shell"
    // reconcile below. Measured live (headless probe, tickets/opus.md): for
    // an early-mounted shell, `state` IS the live root's backing
    // store (direct writes below — `state.workspace = ws`,
    // `state.workspaceReady = true` in the eager scope-fetch step
    // that follows — are visible on the mounted element immediately, not
    // just a pre-mount seed), so those direct writes were observed flipping
    // `workspaceReady` true up to ~200ms BEFORE the explicit
    // `_bootFromCache: false` reconcile ran, since that reconcile only runs
    // once every downstream scope-fetch has also settled. No consumer reads
    // `_bootFromCache` yet, so that gap was never user-visible, but the
    // flag's own contract ("true ONLY while the painted chrome is
    // cache-derived", state.js) should not lag the live answer it exists to
    // track. No-op on the cold path (never set true) and on the
    // early-mount-failed path (already reset in its own catch block).
    if (state) state._bootFromCache = false
  }

  // 7. Eager scope fetch — ORG + WORKSPACE.
  // SDK exposes service methods FLAT (sdk.getOrganization, sdk.getWorkspace);
  // the previous sdk.organization?.get?.() chain was dead code — optional
  // chaining short-circuited on the missing namespace, returning undefined
  // and silently leaving state.org/workspace null. That null forced sub-apps
  // to refetch on render, which is one of the storm root causes.
  // workspaceProject has no get-by-id flat method (the service exposes
  // nested namespaces — tickets, chat, etc.) so we no longer attempt to
  // hydrate state.workspaceProject here.
  // Skip the eager scope fetch when running as a public surface (no
  // user) — the org/workspace lookups would 401 and waste a round-trip.
  // `_completeWorkspaceAccess` — set by "the caller's access" below when the
  // boot's record still owes its `access`; returned on the result.
  let _completeWorkspaceAccess = null
  if (sdk && state && state.user) {
    // Active org now lives on `user.activeOrganization` (Mongo). The
    // legacy `localStorage.activeOrg` key was replaced by the
    // `PATCH /auth/me/active-org` flow + socket.io cross-session sync.
    // Falls back to null when the user hasn't picked one yet — sub-apps
    // then default to `orgs[0]` via their own resolvers.
    const activeOrgId = state.user.activeOrganization
      ? String(state.user.activeOrganization)
      : null
    // The org's workspace list. `claims.workspaces` comes ONLY from the
    // governance JWT, so an SDK-only session (Google/OAuth, or any session
    // before federation lands) arrives here with an EMPTY list — which breaks
    // two things at once: the URL's `?ws=` handle has nothing to resolve
    // against, so a deep link silently falls back to the org's HQ workspace,
    // and the workspace switcher renders "no workspaces to load". One list
    // fetch, only when the claims list is actually empty, fixes both and then
    // seeds the snapshot so every downstream consumer sees the real list.
    const _mapWsRows = (rows, orgId) =>
      (Array.isArray(rows) ? rows : [])
        .map((w) => ({
          id: String(w._id || w.id || ''),
          // `displayName` FIRST — the legacy `name` field is frozen at
          // "Default" on older records and hid every rename. THE rule lives in
          // activeWorkspaceRecord.js; do not re-derive it here.
          name: workspaceDisplayName(w),
          slug: w.slug,
          key: w.key,
          type: w.type,
          org_id: String(w.organization || w.org_id || orgId),
          role: w.role || 'member',
          // List row — no `settings`. See tickets/spec-workspace-record-shape.md.
          _shape: 'list'
        }))
        .filter((w) => w.id)
    // WHITELABEL-STRICT-TENANT-SCOPE-1 — every list below is narrowed to the
    // bound host's ONE workspace AS IT IS BUILT. The composite boot computes
    // its list SERVER-side for the account's stored active org, which on a
    // white-label host is whatever org my.symbols last used — adopting it raw
    // seeded another tenant's workspaces into the switcher, and a non-empty
    // foreign list also skipped the fetch of the host org's own list. Scoped
    // here, a foreign list comes out empty and the fetch below runs for the
    // host org (`activeOrgId` is the host's: the boot stated it above).
    // Identity on platform hosts (no scope).
    const _listHostScope = whitelabelHostScopeOf()
    const _hostScopedList = (list) => scopeWorkspaceListToWhitelabelHost(list, _listHostScope)
    let _wsList = context.preResolvedAuth?.workspaces
    // Composite-boot fast path: the org's workspace list already rode the
    // one-RTT payload — no second fetch. (Boot's list is the ACTIVE org's,
    // same as this fetch would target.)
    if (!(Array.isArray(_wsList) && _wsList.length) && _bootSections?.workspaces) {
      const bootRows =
        _bootSections.workspaces.items ||
        (Array.isArray(_bootSections.workspaces) ? _bootSections.workspaces : [])
      const bootList = _hostScopedList(_mapWsRows(bootRows, activeOrgId))
      if (bootList.length) {
        _wsList = bootList
        if (context.preResolvedAuth) context.preResolvedAuth.workspaces = bootList
      }
    }
    if (activeOrgId && !(Array.isArray(_wsList) && _wsList.length)) {
      try {
        const res = await withBootDeadline(
          () => sdk.listWorkspaces?.({ organization: activeOrgId }),
          callDeadlineMs,
          'listWorkspaces'
        )
        const rows = (res && (res.items || res.data?.items || res.data || res.workspaces)) || []
        const list = _hostScopedList(_mapWsRows(rows, activeOrgId))
        if (list.length) {
          _wsList = list
          if (context.preResolvedAuth) context.preResolvedAuth.workspaces = list
        }
      } catch (err) {
        console.warn('[bootShell] workspaces list fetch failed:', err?.message || err)
      }
    }
    // The snapshot's `workspace` was resolved before the host's row was in
    // any list; re-scope it against the list now held, so the host's own row
    // (never a sibling's) is the boot's workspace.
    if (_listHostScope !== null && context.preResolvedAuth && Array.isArray(_wsList)) {
      context.preResolvedAuth = scopeSnapshotToWhitelabelHost(
        { ...context.preResolvedAuth, workspaces: _hostScopedList(_wsList) },
        _listHostScope
      )
    }
    // Persist what boot fetched onto the state seed: the shell's handle
    // resolver (registerWorkspaceHandleResolver) and the scheme-A route heal
    // both read root.workspaces / root.orgWorkspaces at runtime — in
    // SDK-only sessions the claims tick never fills these (no governance
    // JWT), so without this seed the resolver stays blind and a 2-segment
    // URL's route re-dispatch never fires.
    if (Array.isArray(_wsList) && _wsList.length && state) {
      state.workspaces = _wsList
      const byOrg = {}
      for (const w of _wsList) {
        if (!w?.org_id) continue
        ;(byOrg[w.org_id] = byOrg[w.org_id] || []).push(w)
      }
      state.orgWorkspaces = { ...(state.orgWorkspaces || {}), ...byOrg }
    }
    // Scheme A URL-org scope (BOOT-RESHUFFLE F1 — the "Phase 3 refinement"
    // resolveActiveWorkspace's cross-org fallthrough comment promised). The
    // URL names the org scope: when `/w/<slug>` resolves to a MEMBER org
    // different from the server-active one, boot must scope to the URL's org
    // BEFORE first render. This fetch used to be gated on the URL also naming
    // a workspace handle (seg2 / legacy `?ws=`), so a bare `/w/<org>` booted
    // the PERSISTED org's chrome + module + dashboard under the URL's org, and
    // a post-render reconcile (switchOrg → ~110-key root.replace → refetch)
    // redid the whole boot in front of the user — the "few refreshes and
    // widgets never settle" landing (tickets/workspace.md BOOT-RESHUFFLE).
    // The handle-resolution case (same-org deep link naming a workspace) keeps
    // its fetch too — one list read serves both.
    let _urlOrgWs = null
    try {
      const loc = typeof window !== 'undefined' ? window.location : null
      const rawSeg = getWorkspaceRawSegments(loc?.pathname || '')
      const wantHandle = rawSeg.seg2 || getWorkspaceQueryParam(loc?.search || '')
      if (rawSeg.orgSlug && typeof sdk.listWorkspaces === 'function') {
        const urlOrg = (context.preResolvedAuth?.orgs || []).find(
          (o) => o && (o.slug === rawSeg.orgSlug || o.username === rawSeg.orgSlug)
        )
        const urlOrgId = urlOrg ? String(urlOrg.id ?? urlOrg._id ?? '') : null
        const crossOrg = !!urlOrgId && urlOrgId !== String(activeOrgId || '')
        // A deep link INTO the active org reuses the list boot already holds
        // (the boot report's E: it re-listed the same org, ~0.24 s).
        if (urlOrgId && !crossOrg && wantHandle && Array.isArray(_wsList) && _wsList.length) {
          _urlOrgWs = { [urlOrgId]: _wsList }
          if (state) {
            state.orgWorkspaces = { ...(state.orgWorkspaces || {}), ..._urlOrgWs }
          }
        } else if (urlOrgId && (crossOrg || wantHandle)) {
          const res2 = await withBootDeadline(
            () => sdk.listWorkspaces({ organization: urlOrgId }),
            callDeadlineMs,
            'listWorkspaces:urlOrg'
          )
          const rows2 =
            (res2 && (res2.items || res2.data?.items || res2.data || res2.workspaces)) || []
          const list2 = _mapWsRows(rows2, urlOrgId)
          if (list2.length) {
            _urlOrgWs = { [urlOrgId]: list2 }
            // Same persistence rationale as the active-org list above.
            if (state) {
              state.orgWorkspaces = { ...(state.orgWorkspaces || {}), ..._urlOrgWs }
            }
          }
        }
        if (crossOrg && isMwtEnabled()) {
          // Resolve the landing workspace INSIDE the URL org (URL handle →
          // tab pointer-in-org → HQ) so the canonical switch below can hop
          // the active workspace in the same move.
          const _landing = resolveActiveWorkspace({
            url: loc?.pathname || '',
            search: loc?.search || '',
            orgs: context.preResolvedAuth?.orgs,
            workspaces: (_urlOrgWs && _urlOrgWs[urlOrgId]) || [],
            orgWorkspaces: _urlOrgWs || undefined
          })
          const _landingWsId =
            _landing && String(_landing.orgId || '') === urlOrgId ? _landing.workspaceId : null
          // Align org identity PRE-RENDER through the canonical entry point.
          // sdk.switchOrg persists Mongo User.activeOrganization (the landing
          // is a deliberate org activation — the next login lands here
          // directly instead of replaying the cross-org dance every time,
          // which is how this bug kept "coming back"), sets
          // sdk._context.activeOrgId (so reconcileMwtOrgFromUrl finds nothing
          // to reconcile), invalidates cached token claims (server-side
          // token-scoped reads — users.members.list et al — re-mint onto this
          // org), and hops the active workspace when the landing target is
          // known. No rootBus listeners are registered yet (create() runs
          // later), so no teardown pipeline fires — this is pre-render scope
          // setting, not a live switch.
          try {
            await withBootDeadline(
              () =>
                sdk.switchOrg(
                  urlOrgId,
                  _landingWsId ? { homeWorkspaceId: _landingWsId } : { skipWorkspaceHop: true }
                ),
              callDeadlineMs,
              'switchOrg'
            )
          } catch (err) {
            console.warn(
              '[bootShell] url-org pre-render switch failed (post-render reconcile heals):',
              err?.message || err
            )
          }
          // Recompute the snapshot URL-pinned — _resolveAuthSnapshot mirrors
          // auth.js resolveActiveOrgId's URL pin, so the first auth tick
          // agrees with this scope instead of reverting it.
          //
          // Reads/writes `state.user` here — NOT the `_me` closure
          // binding — even though they're the same object (`state.user
          // = _me` ran earlier in this function, guarded by the same
          // `state.user` truthiness this whole block is gated on, see
          // the `if (sdk && state && state.user)` guard
          // above). A direct reference to `_me` this deep in nested
          // try/if/if/await control flow tripped a Parcel/SWC scope-hoisting
          // rename bug: the renamer correctly rewrote the `_me` declaration
          // and every EARLIER read, but silently left these two later reads
          // at their pre-rename name, producing a live `ReferenceError: me
          // is not defined` on cold-boot deep links that name a different
          // org than the user's persisted `activeOrganization` (tickets/
          // workspace.md "bootShell: `me is not defined`", 2026-08-08;
          // confirmed via bundle inspection — the renamer's declaration
          // became `_me1` while these two sites stayed literally `_me`, i.e.
          // whatever local name is chosen, NOT a naming collision). Routing
          // through `state.user` — already used for `activeOrgId`
          // a few lines up in this same block — sidesteps the bug: it's a
          // plain property path off `context`, which every other line in
          // this 170-line async function already threads through cleanly,
          // rather than a value carried purely by closure across many nested
          // blocks and `await` boundaries.
          state.user.activeOrganization = urlOrgId
          context.preResolvedAuth = scopeSnapshotToWhitelabelHost(
            resolveAuthSnapshot(state.user, {
              urlOrgId,
              urlOrgWorkspaces: (_urlOrgWs && _urlOrgWs[urlOrgId]) || []
            }),
            whitelabelHostScopeOf()
          )
          // Re-point the pre-create seeds at the URL org's list — the seed
          // block above ran with the persisted org's.
          const _snapWs = context.preResolvedAuth?.workspaces
          if (Array.isArray(_snapWs) && _snapWs.length) {
            _wsList = _snapWs
            if (state) state.workspaces = _snapWs
          }
        }
      }
    } catch (err) {
      console.warn('[bootShell] url-org workspaces fetch failed:', err?.message || err)
    }
    // Multi-workspace-tabs (§6e/§6h): resolve THIS tab's workspace — URL
    // `/w/<org>/<ws-handle>` (or legacy `?ws=`) wins, then the per-tab
    // pointer (sessionStorage when MWT on), then the pre-resolved claim.
    // Byte-identical to the old `localStorage.getItem('activeWorkspace')`
    // when the flag is off.
    const _wsResolved = resolveActiveWorkspace({
      url: (typeof window !== 'undefined' && window.location && window.location.pathname) || '',
      // The workspace handle is what makes the URL name a WORKSPACE and not
      // just an org — required for orgs holding more than one, where the
      // prefix alone resolves to HQ and silently drops the user into the
      // wrong workspace.
      search: (typeof window !== 'undefined' && window.location && window.location.search) || '',
      orgs: context.preResolvedAuth?.orgs,
      workspaces: _wsList,
      orgWorkspaces: _urlOrgWs || undefined,
      // The precedence's last rung, named in the comment above and never
      // passed: with no URL pin and no pointer (a fresh browser, a cleared
      // store, another origin) the snapshot's workspace IS this boot's
      // workspace — root.workspace is seeded from it after mount — so the SDK
      // scope below must be it too, or every scope-derived ticket call (get,
      // update, the attachment upload) goes out unscoped until the first auth
      // tick.
      claim: context.preResolvedAuth?.jwtClaims?.active_workspace_id || null
    })
    // WHITELABEL-STRICT-TENANT-SCOPE-1 — on a white-label host the boot's
    // workspace can only be the HOST's (whatever the pointer, claim or URL
    // handle said), and only for a MEMBER of the host's org — the scoped
    // snapshot's org list is that membership test (whitelabelScope.js empties
    // it for a non-member). `false` (tenant not established) resolves none.
    // Identity on platform hosts.
    const _hostMember =
      !!_listHostScope &&
      (context.preResolvedAuth?.orgs || []).some(
        (o) => String(o?.id ?? o?._id ?? '') === String(_listHostScope.organizationId)
      )
    const _resolvedWsId = _wsResolved.workspaceId
    const activeWorkspaceId =
      _listHostScope === null
        ? _resolvedWsId
        : _hostMember &&
            _resolvedWsId &&
            (!_listHostScope.workspaceId ||
              String(_resolvedWsId) === String(_listHostScope.workspaceId))
          ? _resolvedWsId
          : null
    // The session's OWN org, for a boot that resolved nothing at all: no URL
    // member org, no tab pointer, no claim (`source: 'none'` — a fresh tab on a
    // foreign `/w/<slug>` in a session with no governance workspace list, dev.my
    // 2026-10-30). No other writer seeds the SDK's org there, so every
    // org-scoped read of that session went out with none, and
    // reconcileMwtOrgFromUrl waited out its ladder for a comparand. It is the
    // value the pointer and claim boots write in the first branch below
    // (`activeOrgId`), and only on a platform host (a white-label non-member
    // keeps the empty tenant WHITELABEL-STRICT-TENANT-SCOPE-1 hands them) and
    // only for an org in this session's own list (a stored org the user has
    // left is never seeded).
    const _ownOrgSeedId =
      _listHostScope === null &&
      activeOrgId &&
      (context.preResolvedAuth?.orgs || []).some(
        (o) => String(o?.id ?? o?._id ?? '') === activeOrgId
      )
        ? activeOrgId
        : null
    if (isMwtEnabled() && activeWorkspaceId) {
      // Scope THIS tab's SDK calls to its workspace via the CLIENT-only
      // updateContext (no server mutation, no cross-tab broadcast — services read
      // `_context.activeWorkspaceId` + thread it as the Phase-0 `?workspaceId=`
      // param), and pin the per-tab pointer (sessionStorage only).
      // Also pin the SDK's ORG context: reconcileMwtOrgFromUrl compares the URL
      // slug against sdk._context.activeOrgId, which nothing seeded on a
      // matched-org landing — the reconcile then burned a 25×200ms retry
      // ladder waiting for a comparand before silently giving up. (On the
      // cross-org path the pre-render switchOrg above already set it — this
      // write is idempotent there.)
      try {
        const _ctxOrgId = String(_wsResolved.orgId || activeOrgId || '') || null
        sdk.updateContext?.(
          _ctxOrgId ? { activeWorkspaceId, activeOrgId: _ctxOrgId } : { activeWorkspaceId }
        )
      } catch (err) {
        console.warn('[bootShell] mwt updateContext failed:', err?.message || err)
      }
      setActiveWorkspacePointer(activeWorkspaceId)
    } else if (!isMwtEnabled() && activeWorkspaceId) {
      // Tabs OFF: the same client-only scope write, for the same reason —
      // `_workspaceScope()` reads `_context.activeWorkspaceId` first, so the
      // SDK and root.workspace name one workspace from the first call. No
      // pointer write here: with tabs off that pointer is SHARED, a write
      // would switch every other open tab, and the auth tick already mirrors
      // it.
      try {
        sdk.updateContext?.({ activeWorkspaceId })
      } catch (err) {
        console.warn('[bootShell] updateContext failed:', err?.message || err)
      }
    } else if (isMwtEnabled() && (_wsResolved.orgId || _ownOrgSeedId)) {
      // P0 tenant isolation — `source: 'url-org-unresolved'`: the URL named a
      // member org but no workspace in it is resolvable yet (see
      // resolveActiveWorkspace). Pin the SDK's ORG context to the URL org
      // anyway so org-scoped reads (docs/notes, announcements, roster) follow
      // the TAB's org from the first fetch instead of the caller's global
      // active-claim — and so a failed pre-render switchOrg can't leave the
      // context on the previous tenant. The per-tab pointer is deliberately
      // NOT written (there is no workspace to point at); the auth tick's
      // listWorkspaces fallback resolves one within this boot. With no URL
      // org (`source: 'none'`) the same write pins the session's own org
      // (`_ownOrgSeedId` above); the URL org always wins.
      try {
        sdk.updateContext?.({ activeOrgId: String(_wsResolved.orgId || _ownOrgSeedId) })
      } catch (err) {
        console.warn('[bootShell] mwt org-only updateContext failed:', err?.message || err)
      }
    }

    const scopeFetches = []

    // When a URL slug drove the workspace pick (`_wsResolved.orgId` is set
    // only then), THAT org is this tab's org — seed state.org from it so a
    // cross-org deep link doesn't paint the server-active org's chrome for a
    // beat before reconcileMwtOrgFromUrl converges.
    //
    // WHITELABEL-STRICT-TENANT-SCOPE-1 — on a white-label host the org this
    // seeds is the HOST's, or none: a member's `activeOrgId` already IS the
    // host org (the boot stated it), and for a NON-member it is their stored
    // org — fetching that would paint another tenant's identity under this
    // customer's domain. Identity on platform hosts.
    const _scopeOrgCandidate = _wsResolved.orgId || activeOrgId
    const scopeOrgId =
      _listHostScope === null
        ? _scopeOrgCandidate
        : _hostMember &&
            _scopeOrgCandidate &&
            String(_scopeOrgCandidate) === String(_listHostScope.organizationId)
          ? _scopeOrgCandidate
          : null
    // Composite-boot fast path: boot's `org` section IS the active org — use
    // it without a fetch when the scope didn't get URL-redirected to a
    // different org (cross-org deep links keep the legacy fetch).
    const _bootOrg = _bootSections?.org || null
    const _bootOrgId = _bootOrg ? String(_bootOrg.id ?? _bootOrg._id ?? '') : null
    if (_bootOrg && scopeOrgId && _bootOrgId === String(scopeOrgId)) {
      state.org = _bootOrg
    } else if (scopeOrgId && typeof sdk.getOrganization === 'function') {
      scopeFetches.push(
        withBootDeadline(() => sdk.getOrganization(scopeOrgId), callDeadlineMs, 'getOrganization')
          .then((res) => {
            state.org = res?.data || res || null
          })
          .catch(() => null)
      )
    }

    // Composite-boot fast path: boot's `workspace` section is the record for
    // the SERVER-resolved scope (the pointer we sent). Adopt it without a
    // fetch only when the CLIENT resolution (URL handle > legacy ?ws >
    // pointer) landed on the same workspace — deep links to a different
    // workspace keep the legacy fetch below.
    const _bootWs = _bootSections?.workspace || null
    const _bootWsId = _bootWs ? String(_bootWs._id ?? _bootWs.id ?? '') : null
    const _bootWsMatches = !!(
      _bootWsId &&
      activeWorkspaceId &&
      _bootWsId === String(activeWorkspaceId)
    )
    if (_bootWsMatches) {
      // Same normalization the fetch path applies (server uses _id /
      // displayName / organization; consumers read id / name / org_id), plus
      // the FULL-shape provenance stamp — see activeWorkspaceRecord.js /
      // tickets/spec-workspace-record-shape.md. This is the pre-mount seed
      // path (no live `root` yet to run through `setActiveWorkspaceRecord`);
      // the stamp is what lets the post-mount auth tick's guarded setter
      // recognize this as a complete record and refuse to downgrade it.
      const ws = stampWorkspaceShape(normalizeWorkspaceRecord(_bootWs), 'full', { clone: false })
      state.workspace = ws
      state.workspaceReady = true
      const snap = context.preResolvedAuth
      if (snap) {
        snap.workspace = ws
        if (snap.jwtClaims) snap.jwtClaims.active_workspace_id = activeWorkspaceId
      }
    }
    // The boot resolved ANOTHER workspace (a pointer-less boot falls back to
    // the user's stored active workspace) but the URL's workspace is a row of
    // the boot's own list: adopt that row — it is the full read projection —
    // so first paint does not wait for a second read (the boot report's F,
    // ~0.22 s). Members / prefs stay unprimed below: they describe the boot's
    // workspace.
    // The RAW boot list rows: the server lists with the SAME projection as a
    // single getWorkspace read (WORKSPACE_READ_PROJECTION + platform defaults
    // + resolvedAppearance) — everything but the caller's `access`, which
    // only the single read adds. The row is complete for the chrome; its
    // `access` is completed in the background right after this step ("the
    // caller's access" below).
    const _bootListRows = _bootSections?.workspaces
      ? _bootSections.workspaces.items ||
        (Array.isArray(_bootSections.workspaces) ? _bootSections.workspaces : [])
      : []
    const _bootRow =
      !_bootWsMatches && activeWorkspaceId
        ? _bootListRows.find((w) => w && String(w._id ?? w.id ?? '') === String(activeWorkspaceId))
        : null
    if (_bootRow) {
      const ws = stampWorkspaceShape(normalizeWorkspaceRecord({ ..._bootRow }), 'full', { clone: false })
      state.workspace = ws
      state.workspaceReady = true
      const snap = context.preResolvedAuth
      if (snap) {
        snap.workspace = ws
        if (snap.jwtClaims) snap.jwtClaims.active_workspace_id = activeWorkspaceId
      }
    } else if (!_bootWsMatches && activeWorkspaceId && typeof sdk.getWorkspace === 'function') {
      // localStorage.activeWorkspace is the user's explicit choice and the
      // canonical pointer. The previous code gated on the user's claims/
      // app_metadata workspace list and CLEARED the pointer when the chosen
      // workspace wasn't in it — but that list is incomplete/stale (it omits a
      // freshly-created workspace, and the JWT isn't re-minted on switch), so a
      // valid switch to such a workspace was silently wiped on refresh. The SDK
      // is the real accessibility authority: fetch the chosen workspace and let
      // the server resolve it. On success → seed state AND correct
      // preResolvedAuth (which onCreate seeds into the live root after mount) so
      // jwtClaims.active_workspace_id + workspace agree on the chosen workspace
      // from first paint.
      //
      // Do NOT clear localStorage.activeWorkspace on error here. A boot-time
      // failure is frequently transient (token / governance federation not
      // ready yet, esp. for a workspace not in the JWT scope), and wiping a
      // VALID pointer silently reverts the user's selection on refresh — the
      // exact bug this fixes. The auth.js listWorkspaces fallback validates the
      // pointer against the org's real workspace list and self-corrects it when
      // it is genuinely gone, so a stale pointer still heals within one boot.
      scopeFetches.push(
        withBootDeadline(
          () =>
            getWorkspaceCached
              ? getWorkspaceCached(sdk, activeWorkspaceId)
              : sdk.getWorkspace(activeWorkspaceId),
          callDeadlineMs,
          'getWorkspace'
        )
          .then((res) => {
            const raw = res?.data || res || null
            if (!raw) return
            // Normalize the server record (it uses `_id` / `displayName`) so
            // every consumer that reads `workspace.id` / `workspace.name` — and
            // the auth-tick preserve guard in subscribeAuthSession — sees a
            // consistent shape and keeps this full record. `_shape:'full'` is
            // the provenance stamp `setActiveWorkspaceRecord` (post-mount) and
            // the pre-mount seed below both key off — see
            // tickets/spec-workspace-record-shape.md.
            const ws = stampWorkspaceShape(normalizeWorkspaceRecord(raw), 'full', { clone: false })
            state.workspace = ws
            state.workspaceReady = true
            const snap = context.preResolvedAuth
            if (snap) {
              snap.workspace = ws
              if (snap.jwtClaims) snap.jwtClaims.active_workspace_id = activeWorkspaceId
            }
          })
          .catch((err) => {
            console.warn('[bootShell] eager getWorkspace failed:', err?.message || err)
            return null
          })
      )
    }

    // Hand the remaining composite-boot sections to loadPrivateData through
    // the context (shared→workspace layering: the workspace layer reads
    // context, never the reverse). SCOPE GUARD: members/prefs were scoped
    // server-side by the workspace id the boot REQUEST carried (the
    // pointer); only prime them when the client resolution landed on that
    // same workspace — a URL deep link to a different workspace/org must
    // fall back to its own explicitly-scoped fetches.
    if (_bootSections && _bootWsMatches) {
      context.bootPayload = {
        workspaceId: String(activeWorkspaceId),
        members: Array.isArray(_bootSections.members) ? _bootSections.members : null,
        prefs: _bootSections.prefs || null
      }
    }

    await Promise.all(scopeFetches)

    // THE CALLER'S ACCESS (MODULE-ROLE-CAN-BRIDGES-1). The shell's moduleRole /
    // moduleCan bridges answer from the record's `access` (the role the read
    // was gated on + the member's effective permission keys), and the server
    // adds it ONLY on a single workspace read (WorkspaceController.getWorkspace
    // -> callerAccess). A record adopted above without it — the list row of a
    // deep link to a workspace that is not the account's active one, the row
    // a failed `workspace` section falls back to, a section from a server
    // without it — left moduleRole unknown and moduleCan false for the whole
    // visit for every member who is not an org owner or admin (measured on dev
    // 2026-10-28: role-gated apps sat on their skeleton, with and without the
    // switcher's pointer). ONE rule for every branch above: the boot's record
    // lacks `access` -> read the workspace ONCE, in the BACKGROUND (never
    // awaited: first paint keeps the row's gain) and merge `access` in when it
    // lands. `force` skips the cache, which may hold this very row, and stores
    // the complete record for every later reader; a reader that asks while it
    // flies joins it.
    //
    // Where the answer lands:
    //   • the SEED — `state.workspace` and the snapshot the shell's create pass
    //     seeds into the live root — for any landing before the mount takes it;
    //   • the warm start's already-mounted shell, through the record owner;
    //   • any live root handed to the result's `completeWorkspaceAccess(root)`
    //     (the post-mount step hands the root it mounted), through the owner.
    // Only while the record still names THIS workspace (the person may have
    // moved on), is still full and still lacks `access`; the merge is a copy
    // that keeps every field the record had.
    const _accessWsId = activeWorkspaceId ? String(activeWorkspaceId) : ''
    const _seedWs = state.workspace
    if (
      _accessWsId &&
      _seedWs &&
      _seedWs._shape === 'full' &&
      String(_seedWs.id || _seedWs._id || '') === _accessWsId &&
      !(_seedWs.access && typeof _seedWs.access === 'object') &&
      typeof sdk.getWorkspace === 'function'
    ) {
      const _withAccess = (rec, access) =>
        access &&
        rec &&
        typeof rec === 'object' &&
        rec._shape === 'full' &&
        String(rec.id || rec._id || '') === _accessWsId &&
        !(rec.access && typeof rec.access === 'object')
          ? { ...rec, access }
          : null
      // A consumer that throws inside the root's update must not turn this
      // background step into an unhandled rejection: warn, answer false.
      const _toLiveRoot = (root, access) => {
        const next = root && _withAccess(root.workspace, access)
        if (!next) return false
        try {
          setActiveWorkspaceRecord(root, next, { reason: 'boot-access' })
        } catch (err) {
          console.warn('[bootShell] workspace access merge failed:', err?.message || err)
          return false
        }
        return true
      }
      const _accessRead = Promise.resolve()
        .then(() =>
          getWorkspaceCached
            ? getWorkspaceCached(sdk, _accessWsId, { force: true })
            : sdk.getWorkspace(_accessWsId)
        )
        .then((res) => {
          const raw = res?.data || res || null
          return raw &&
            String(raw._id ?? raw.id ?? '') === _accessWsId &&
            raw.access &&
            typeof raw.access === 'object'
            ? raw.access
            : null
        })
        .catch((err) => {
          console.warn('[bootShell] workspace access read failed:', err?.message || err)
          return null
        })
      _accessRead.then((access) => {
        if (!access) return
        const _seeded = _withAccess(state.workspace, access)
        if (_seeded) state.workspace = _seeded
        const _snap = context.preResolvedAuth
        if (_snap) {
          const _snapNext =
            _seeded && _snap.workspace === _seedWs ? _seeded : _withAccess(_snap.workspace, access)
          if (_snapNext) _snap.workspace = _snapNext
        }
        if (_earlyShell && _earlyShell.state) {
          _toLiveRoot(_earlyShell.state.root || _earlyShell.state, access)
        }
      })
      _completeWorkspaceAccess = (root) => _accessRead.then((access) => _toLiveRoot(root, access))
    }

    // Canonicalize the address bar (scheme A path form — legacy `?ws=` is
    // rewritten to `/w/<org>/<handle>/…`) once the workspace is resolved, so
    // the URL is shareable + reload-stable from FIRST paint rather than only
    // after the first in-app navigation. No-op for single-workspace orgs (the
    // `/w/<slug>` prefix already names those unambiguously), when MWT is off,
    // and for foreign-org URLs (reconcileMwtOrgFromUrl owns those). Siblings
    // come from the URL org's list when that org drove the pick.
    syncWorkspaceUrl({
      workspace: state.workspace,
      org: state.org,
      workspaces: (_urlOrgWs && _wsResolved.orgId && _urlOrgWs[_wsResolved.orgId]) || _wsList
    })
  }

  // 7c. THE WORKSPACE'S DEFAULT THEME at the COLD seed (Nika 2026-10-18,
  // GreenIG: "Dark theme by default"). The full record has landed (7 above)
  // and nothing is mounted yet, so `workspace.settings.theme` joins the seed
  // the cold create() reads (`globalTheme: themeMode`, bootShell step 8) —
  // the first paint is already right, no flip. Same precedence as 5b, decided
  // again from the base: the user's own choice → the workspace → the brand →
  // the OS (a stale warm-snapshot default never survives a cold boot).
  // ⚠️ NEVER on a mounted app: with a warm early shell the theme was seeded
  // from that scope's snapshot and stays — re-theming a mounted app is what
  // parked workspaceConfigs.js `WORKSPACE_DEFAULT_THEME_ENABLED` (bellforge
  // freeze). A changed record is right on the next load (the snapshot is
  // rewritten when the boot settles). Only a FULL record decides: a
  // list-shaped placeholder (the eager fetch missed its deadline) carries no
  // `settings`, and must not erase a default the snapshot seeded.
  if (!_earlyShell && state && state.workspace && state.workspace._shape === 'full') {
    const _coldThemeMode = bootTenantThemeMode({
      baseMode: themeBase,
      userChoseExplicitly: userChoseTheme,
      workspaceMode: workspaceThemeModeOf(state.workspace),
      brandMode: brandThemeMode
    })
    if (_coldThemeMode !== themeMode) {
      themeMode = _coldThemeMode
      state.themeMode = themeMode
      if (typeof document !== 'undefined') {
        state.themeResolved = resolveThemeResolvedNow(document, themeMode)
      }
      bootTrail('bootShell:theme:workspace')
    }
  }

  // 7b. INTERFACE LANGUAGE — resolve it, apply it, and load the bundles.
  //
  // This is the step that never existed. Nothing in the tree ever called
  // `loadI18nNamespace`: the only caller was `setLang`, which loops over
  // `getLoadedNamespaces()` — a set that is populated BY a load. Empty set →
  // `mergeAllNamespaces` returns `{}` → no translation had ever reached the UI
  // in any language. Everything else in the i18n stack was correct and inert.
  //
  // Resolution runs HERE, after the workspace scope resolved, because first
  // paint had no workspace. Precedence (functions/languages.js, THE LAW —
  // Nika 2026-09-10 "default language is english. only workspaces that are
  // specifically set as Georgian should render in georgian"):
  //   workspace enforced > workspace.settings.language > branding.lang > en
  //
  // The member's account preference (`user.preferences.lang`) and this
  // device's `localStorage.lang` used to sit between the enforced level and
  // the workspace default; they no longer decide a workspace's interface
  // language at all. Measured on dev 2026-09-10: the Symbols HQ workspace,
  // which sets no language, rendered Georgian off exactly those two values.
  //
  // The resolved value is applied DIRECTLY rather than through `setLang`.
  // `setLang` is the USER-INITIATED path and it persists — a language the
  // system merely resolved is persisted nowhere: not to the device, not to the
  // account. Nothing in boot writes `localStorage.lang` any more, except the
  // step-8b RESTORE of whatever was there before the warm-mount delivery.
  //
  // Whole block is try-guarded: a language is chrome, and no failure in it may
  // stop the shell from mounting. `_resolvedLang` is declared OUTSIDE the try
  // so step 8 can deliver it to the live element on both mount paths — see
  // "the resolved language must reach the MOUNTED app" there. It falls back to
  // the early value, so a throw in here leaves step 8 with exactly the language
  // first paint already had, never `undefined`.
  let _resolvedLang = lang
  try {
    // The workspace's language pair: the live record's `settings` when the
    // scope fetch produced them, else the warm snapshot's cached pair for the
    // SAME workspace. The fallback matters when the eager fetch failed or
    // returned a record with no `settings`: resolving "set to nothing" there
    // would paint English over the Georgian first paint step 1 already made,
    // and the full record would then paint Georgian a third time.
    const _liveWs = state?.workspace || null
    const _liveWsSettings =
      _liveWs && _liveWs.settings && typeof _liveWs.settings === 'object' ? _liveWs.settings : null
    const _wsLangBag =
      _liveWsSettings ||
      (_cachedWsLanguage &&
      (!_liveWs ||
        String(_liveWs.id || _liveWs._id || '') === String(warmSnapshot.workspaceId || ''))
        ? _cachedWsLanguage
        : null)
    const _wsDefaultLang = _wsLangBag?.language || null
    // The white-label host's own language, the weakest signal in the chain and
    // the only one that exists BEFORE sign-in. `whitelabelBrand` is the public
    // branding projection already awaited above for the theme and the mark, so
    // this costs no extra request. Without it a first-time visitor to a branded
    // login had no signal at all above `en`: the Georgian bundles were shipped
    // and complete, and nothing on the page could ask for them.
    // The seed is bounded at 600ms (a slow lookup must never delay the
    // mount), so on a cold API the brand can still be IN FLIGHT here — and
    // then `whitelabelBrand` is null, the language resolves to `en`, and the
    // late brand re-render restores the mark but never the language.
    // Measured on gita.pr.symbols.app (staging, 2026-09-08): scope said
    // `lang: 'ka'`, hydrated `<html lang="en">`. The language is the one
    // thing a branded host must not boot without, so wait for the SAME
    // lookup to settle, bounded once more so an unreachable API still
    // cannot hold the shell.
    // ONE settle, TWO readings of the result. `settleWhitelabelBrand`
    // (publicBootLang.js) is the shared helper the branded /login bounce also
    // calls, so the record is bound here rather than inlined into the
    // `_brandLang` expression — the enforced level below needs the SAME
    // record, and settling it twice would be a second await on a boot path.
    const _brandRecord = await settleWhitelabelBrand(state)
    const _brandLang = _brandRecord?.lang || null

    // THE ENFORCED LEVEL. Two sources, same shape, same reader
    // (`enforcedWorkspaceLang` — the one place that decides whether the
    // `language` + `languagePolicy` pair is live), tried strongest first:
    //
    //   1. the live workspace record, once the scope has resolved. This is
    //      the authoritative one and the one that matters after sign-in.
    //   2. the branded host's `workspaceLanguage`, carried on the SAME public
    //      custom-domain scope lookup settled just above for the mark and the
    //      theme. It exists so a branded host enforces from FIRST PAINT
    //      instead of booting into the visitor's own language and re-languaging
    //      the page once the workspace record lands. Distinct from
    //      `_brandLang` above, which is the ORG's language and the weakest
    //      signal there is.
    //
    // ⚠️ THE BRANDED /login BOUNCE DELIBERATELY HAS NO ENFORCED LEVEL.
    // That exit never reaches 7b — it resolves through `applyPublicBootLang`,
    // which asks the page's `?lang=`, the brand and the browser, and
    // nothing more. The reason is not that the pair is unavailable there (the
    // same scope carries it): a visitor on a login form is not INSIDE the
    // workspace yet, so a workspace rule has no one to bind. The page's own
    // `?lang=`, the host's `branding.lang` and the visitor's browser are the
    // right signals for that page (KA-1). The enforced level starts at the
    // first authenticated boot, which is this one.
    //
    // Both sources are `null` for every workspace that did not enforce, and
    // then this level does not exist and the four below decide exactly as they
    // always have.
    const _enforcedLang =
      enforcedWorkspaceLang(_wsLangBag) || enforcedWorkspaceLang(_brandRecord?.workspaceLanguage)

    _resolvedLang = resolveLang({
      workspaceEnforced: _enforcedLang,
      workspaceDefault: _wsDefaultLang,
      brandDefault: _brandLang
    })

    // `context.polyglot` is minted in step 1b, BEFORE either mount — `_poly`
    // below is that same object. Nothing here may mint a second one: the app
    // binds whatever `context.polyglot` was at `create()` time, and a later
    // object is written into by boot and read by nobody.

    // Which namespaces this session needs — `sessionI18nNamespaces` (top of
    // this step) computes it: the ONE computation the signed-out /login bounce
    // reads too, so the two exits can never declare different sets.
    const _namespaces = new Set(sessionI18nNamespaces())
    // `setLang` reads this to know which namespaces to load when the language
    // changes. It is the session's declared set, so a switch reloads exactly
    // what this surface uses — not everything the platform ships.
    _poly.namespaces = Array.from(_namespaces)

    // ENGLISH IS BUNDLED; EVERY OTHER LANGUAGE IS A LAZY, PER-NAMESPACE CHUNK.
    //
    // The static English seed moved UP to step 1b — it must land before the
    // first `create()`, because `prepareState` seeds the active language's keys
    // into the root state DURING the mount. `_poly.translations` already
    // carries it by the time this runs.
    //
    // This used to seed English statically and FETCH the rest. Measured live on
    // the running shell, that fetch could never have worked: Parcel compiles a
    // `.json` asset into a `.js` module, so the asset URL for `ka/common`
    // resolved to `/common.f873341d.js`; `res.json()` threw on 31 KB of
    // JavaScript and every `ka` namespace fell back to English. The runtime
    // path `/i18n/ka/common.json` answered 200 with the dev server's SPA
    // fallback HTML — a 200 that is not JSON, which `res.ok` accepts. The
    // language flipped, `translations.ka` stayed at 0 keys, and the UI stayed
    // English with nothing reported. The loader has no network path at all
    // now; the non-English bundles arrive as bundler-resolved chunks.

    // Register the session's namespace set with the loader even on an English
    // boot. `loadI18nNamespace` records every namespace it is asked for, and
    // that record is what a later switch re-loads — without this call an
    // English boot would hand `setLang` an empty set and a switch would fetch
    // `common` alone.
    await loadI18nNamespaces(DEFAULT_LANG, Array.from(_namespaces))

    // The English rescue: when the static map is empty (frank's revived
    // runtime never runs a module body), whatever the payload carries is all
    // there is — `mergeAllNamespaces` reads exactly that.
    if (!Object.keys(_poly.translations[DEFAULT_LANG] || {}).length) {
      _poly.translations[DEFAULT_LANG] = mergeAllNamespaces(DEFAULT_LANG)
    }

    // A non-English boot loads its chunks HERE, in parallel, before the mount —
    // so first paint is already translated instead of flashing English. Only
    // the namespaces this surface declared, never the whole platform.
    if (_resolvedLang !== DEFAULT_LANG) {
      const _merged = await loadI18nNamespaces(_resolvedLang, Array.from(_namespaces))
      if (Object.keys(_merged).length) {
        const _existing = _poly.translations[_resolvedLang] || {}
        // Same collision rule as setLang: a chunk the registry really served is
        // the newer truth; a fallback must never erase a baked translation.
        _poly.translations[_resolvedLang] = _namespaces.size
          ? Array.from(_namespaces).some((ns) => hasI18nBundle(_resolvedLang, ns))
            ? { ..._existing, ..._merged }
            : { ..._merged, ..._existing }
          : _existing
      }
    }

    if (state) state.lang = _resolvedLang
    if (_resolvedLang !== lang && typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = _resolvedLang
      document.documentElement.dir = langDir(_resolvedLang)
    }
    bootTrail('bootShell:i18n')
  } catch (err) {
    console.warn('[bootShell] i18n boot failed:', err?.message || err)
  }


  // no SDK at all (a fail-closed API plane): nobody is signed in
  if (typeof context.bootSignedIn !== 'boolean') context.bootSignedIn = false
  return {
    aborted: false,
    signedIn: context.bootSignedIn,
    themeMode,
    storedThemeMode: prep && prep.themeMode,
    lang: _resolvedLang,
    earlyShell: _earlyShell,
    state,
    storage,
    completeWorkspaceAccess: _completeWorkspaceAccess
  }
}
